// ─── ORDERS SCREEN ─────────────────────────────────────────────
const ORDER_TABLE_CORE_COLUMNS = [
  { id:'order_id', label:'Order ID', width:116 },
  { id:'date', label:'Date', width:90 },
  { id:'name', label:'Name', width:175 },
  { id:'email', label:'Email', width:225 },
  { id:'phone', label:'Phone', width:145 },
  { id:'location', label:'Location', width:170 },
  { id:'product', label:'Product', width:175 },
  { id:'amount', label:'Amount', width:110 },
];

const DEFAULT_ORDER_TABLE_SETTINGS = {
  columnOrder:ORDER_TABLE_CORE_COLUMNS.map(column => column.id),
  hiddenColumns:['email', 'phone'],
  customVariables:[],
  showIgnoredOrders:false,
};

const normalizeOrderTableSettings = value => {
  const raw = value && typeof value === 'object' ? value : {};
  const customVariables = (Array.isArray(raw.customVariables) ? raw.customVariables : [])
    .map(variable => ({
      id:String(variable?.id || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 48),
      label:String(variable?.label || '').trim().slice(0, 80),
      code:String(variable?.code || '').slice(0, 8000),
    }))
    .filter((variable, index, list) => variable.id && variable.label && variable.code && list.findIndex(item => item.id === variable.id) === index)
    .slice(0, 12);
  const allowed = new Set([
    ...ORDER_TABLE_CORE_COLUMNS.map(column => column.id),
    ...customVariables.map(variable => `custom:${variable.id}`),
  ]);
  const columnOrder = [...new Set((Array.isArray(raw.columnOrder) ? raw.columnOrder : []).filter(id => allowed.has(id)))];
  ORDER_TABLE_CORE_COLUMNS.forEach(column => { if (!columnOrder.includes(column.id)) columnOrder.push(column.id); });
  customVariables.forEach(variable => {
    const id = `custom:${variable.id}`;
    if (!columnOrder.includes(id)) columnOrder.push(id);
  });
  const hiddenColumns = [...new Set((Array.isArray(raw.hiddenColumns) ? raw.hiddenColumns : []).filter(id => allowed.has(id)))];
  return {
    columnOrder,
    hiddenColumns,
    customVariables,
    showIgnoredOrders:raw.showIgnoredOrders === true,
  };
};

const EditedOrderMarker = ({ size = 12, style = {} }) => {
  const [tooltip, setTooltip] = useState(null);
  const showTooltip = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({ left: rect.left + rect.width / 2, top: rect.top - 8 });
  };

  return (
    <span
      aria-label="This order has been edited"
      onMouseEnter={showTooltip}
      onFocus={showTooltip}
      onMouseLeave={() => setTooltip(null)}
      onBlur={() => setTooltip(null)}
      tabIndex={0}
      style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#006FDD', lineHeight:1, flexShrink:0, verticalAlign:'text-bottom', outline:'none', ...style }}
    >
      <Edit3 size={size} strokeWidth={2.4}/>
      {tooltip && (
        <span style={{ position:'fixed', left:tooltip.left, top:tooltip.top, transform:'translate(-50%, -100%)', background:'#111827', color:'#fff', borderRadius:6, padding:'5px 8px', fontFamily:'Inter,sans-serif', fontSize:11, fontWeight:700, lineHeight:1.2, whiteSpace:'nowrap', boxShadow:'0 8px 18px rgba(17,24,39,0.18)', zIndex:1000, pointerEvents:'none' }}>
          This order has been edited
        </span>
      )}
    </span>
  );
};

const isDuplicateReviewOrder = order => order?.duplicate_review_status === 'suspected' && normalizeDashboardOrderStatus(order?.status) === 'problem';

const FULFILLMENT_STATUS_META = {
  unrouted: { label:'Unrouted', color:'#1E3A8A', dotColor:'#2563EB', filled:false },
  needs_review: { label:'Needs review', color:'#92400E', dotColor:'#D97706', filled:true },
  routed: { label:'Ready to export', color:'#1E3A8A', dotColor:'#2563EB', filled:false },
  sent: { label:'Routed', color:'#166534', dotColor:'#16A34A', filled:false },
  ignored: { label:'Ignored', color:'#6B7280', dotColor:'#9CA3AF', filled:false },
  handoff_failed: { label:'Error', color:'#991B1B', dotColor:'#DC2626', filled:true },
};

const ORDER_STATUS_EDIT_OPTIONS = [
  { value:'paid', label:'Route by rules', colors:['#64748B'] },
  { value:'problem', label:'Needs review', colors:['#A16207'] },
  { value:'ignored', label:'Ignored', colors:['#B6BDC8'] },
];

const BULK_STATUS_PATCH = {
  reroute:'paid',
  mark_sent:'routed',
  mark_needs_review:'problem',
  ignore:'ignored',
};

const fulfillmentRoutesForOrder = order => {
  const routes = Array.isArray(order?.fulfillment_destinations)
    ? order.fulfillment_destinations
    : order?.fulfillment_destination
    ? [order.fulfillment_destination]
    : [];
  return routes.filter(Boolean);
};

const orderReviewIssues = order => {
  if (normalizeDashboardOrderStatus(order?.status) === 'ignored') return [];
  const issues = [];
  const addIssue = issue => {
    if (!issue?.type || issues.some(existing => existing.type === issue.type && existing.detail === issue.detail)) return;
    issues.push(issue);
  };
  (Array.isArray(order?.review_issues) ? order.review_issues : []).forEach(addIssue);
  if (isDuplicateReviewOrder(order)) {
    addIssue({
      type:'duplicate',
      label:'Possible duplicate',
      detail:order.duplicate_reason || 'This order looks similar to another order and needs review.',
    });
  }
  if (normalizeDashboardOrderStatus(order?.status) === 'problem' && !isDuplicateReviewOrder(order)) {
    addIssue({
      type:'status',
      label:'Marked for review',
      detail:'This order is marked for review and will not be included in active exports.',
    });
  }
  const routes = fulfillmentRoutesForOrder(order);
  if (normalizeDashboardOrderStatus(order?.status) === 'routed' && !routes.length) {
    addIssue({
      type:'sent_destination_missing',
      label:'Destination missing',
      detail:'This order is marked routed but no destination can be resolved.',
    });
  }
  const items = orderItemsForDisplay(order);
  if (items.some(item => !item.product_id)) {
    addIssue({
      type:'product_mapping',
      label:'Product mapping missing',
      detail:'One or more fulfillable items are not mapped to a product.',
    });
  }
  if (items.some(item => item.product_id && !String(item.display_sku || item.sku || item.product_sku || '').trim())) {
    addIssue({
      type:'missing_sku',
      label:'Missing SKU',
      detail:'One or more fulfillable products do not have a SKU for warehouse export.',
    });
  }
  const requiredAddress = [order?.address_line1, order?.city, order?.postal_code, order?.country];
  if (requiredAddress.some(value => !String(value || '').trim())) {
    addIssue({
      type:'missing_address',
      label:'Shipping address incomplete',
      detail:'Address line 1, city, postal code, and country are required before fulfillment.',
    });
  }
  if (['invalid', 'review'].includes(order?.address_check_status)) {
    addIssue({
      type:'address_invalid',
      label:'Address needs review',
      detail:order.address_check_reason || 'Google could not verify this shipping address.',
    });
  }
  if (!String(order?.phone || '').trim()) {
    addIssue({
      type:'missing_phone',
      label:'Missing phone number',
      detail:'No phone number on file for this order.',
    });
  }
  const recipientName = [order?.first_name, order?.last_name].map(value => String(value || '').trim()).filter(Boolean).join(' ');
  const nameLooksLikeAddress = recipientName && (
    /\d/.test(recipientName)
    || (String(order?.address_line1 || '').trim() && recipientName.toLowerCase() === String(order.address_line1).trim().toLowerCase())
  );
  if (nameLooksLikeAddress) {
    addIssue({
      type:'name_review',
      label:'Recipient name needs review',
      detail:`The recipient name "${recipientName}" looks like an address or placeholder, not a person's name.`,
    });
  }
  const country = cleanLocationPart(order?.country).toUpperCase();
  const postal = String(order?.postal_code || '').trim();
  if (country === 'US' && postal && !/^\d{5}(-\d{4})?$/.test(postal)) {
    addIssue({
      type:'postal_code',
      label:'Postal code format',
      detail:'US postal codes should use 5 digits or ZIP+4 format.',
    });
  }
  const orderCurrency = String(order?.currency || '').trim().toLowerCase();
  if (orderCurrency && items.some(item => item.currency && String(item.currency).trim().toLowerCase() !== orderCurrency)) {
    addIssue({
      type:'currency_mismatch',
      label:'Currency mismatch',
      detail:'At least one line item currency differs from the order currency.',
    });
  }
  return issues;
};

const fulfillmentStatusForOrder = (order, issues = orderReviewIssues(order)) => {
  const status = normalizeDashboardOrderStatus(order?.status);
  if (status === 'ignored') return 'ignored';
  if (issues.length) return 'needs_review';
  const apiStatus = String(order?.fulfillment_status || '').trim().toLowerCase();
  if (apiStatus === 'pending_route') return 'unrouted';
  if (apiStatus === 'exported') return 'sent';
  if (FULFILLMENT_STATUS_META[apiStatus]) return apiStatus;
  const routes = fulfillmentRoutesForOrder(order);
  if (status === 'routed') return routes.length ? 'sent' : 'needs_review';
  if (fulfillmentRoutesForOrder(order).length) return 'routed';
  return 'unrouted';
};

const fulfillmentPipelineStageForOrder = (order, issues = orderReviewIssues(order)) => {
  const status = fulfillmentStatusForOrder(order, issues);
  if (status === 'ignored') return 'ignored';
  if (status === 'needs_review') return 'needs_review';
  if (status === 'routed') return 'routed';
  if (status === 'sent') return 'sent';
  return 'unrouted';
};

const orderMatchesPipelineStage = (order, stage, issues = orderReviewIssues(order)) => {
  if (stage === 'all') return true;
  return fulfillmentPipelineStageForOrder(order, issues) === stage;
};

const PIPELINE_STAGES = [
  { value:'unrouted', label:'Unrouted', dotColor:'#2563EB' },
  { value:'needs_review', label:'Needs review', dotColor:'#D97706' },
  { value:'routed', label:'Ready to export', dotColor:'#2563EB' },
  { value:'sent', label:'Routed', dotColor:'#16A34A' },
  { value:'ignored', label:'Ignored', dotColor:'#9CA3AF' },
];

// Only these two pipeline stages are surfaced to the user as filters/flags.
// Routing mechanics (unrouted/ready-to-export/routed) are internal plumbing
// used only to compute export eligibility, not something shown per order.
const FLAG_STAGES = PIPELINE_STAGES.filter(stage => stage.value === 'needs_review' || stage.value === 'ignored');

const ORDER_FLAG_META = {
  needs_review: { label:'Needs review', color:'#92400E', bg:'#FEF3C7', border:'#FDE68A' },
  ignored: { label:'Ignored', color:'#374151', bg:'#F3F4F6', border:'#E5E7EB' },
};

// The only two "flags" a normal order can carry; anything else (unrouted,
// matched a rule, actually exported) is treated as the default, untagged state.
const OrderFlagTag = ({ order, issues = orderReviewIssues(order) }) => {
  const stage = fulfillmentPipelineStageForOrder(order, issues);
  const meta = ORDER_FLAG_META[stage];
  if (!meta) return null;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'2px 8px', borderRadius:6, background:meta.bg, color:meta.color, border:`1px solid ${meta.border}`, fontSize:11, fontWeight:800, whiteSpace:'nowrap', lineHeight:1.2 }}>
      {meta.label}
    </span>
  );
};

// Routine export state stays internal. Only actionable exceptions are shown.
const FulfillmentStatusBadge = ({ status }) => {
  if (status === 'unrouted' || status === 'routed' || status === 'sent') return null;
  const meta = FULFILLMENT_STATUS_META[status] || FULFILLMENT_STATUS_META.unrouted;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', gap:5, minHeight:22, padding:'2px 8px', borderRadius:6, background:'#fff', color:meta.color, border:'1px solid #E5E7EB', fontSize:11, fontWeight:800, whiteSpace:'nowrap', lineHeight:1.2 }}>
      <span aria-hidden="true" style={{ width:8, height:8, borderRadius:'50%', background:meta.filled ? meta.dotColor : '#fff', border:`1.7px solid ${meta.dotColor}`, flexShrink:0 }}/>
      {meta.label}
    </span>
  );
};

const ReviewIssueMarker = ({ issues = [], size = 14, style = {} }) => {
  const [tooltip, setTooltip] = useState(null);
  if (!issues.length) return null;
  const showTooltip = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({ left: rect.left + rect.width / 2, top: rect.top - 8 });
  };
  return (
    <span
      aria-label="Needs review"
      onMouseEnter={showTooltip}
      onFocus={showTooltip}
      onMouseLeave={() => setTooltip(null)}
      onBlur={() => setTooltip(null)}
      tabIndex={0}
      style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#B45309', lineHeight:1, flexShrink:0, verticalAlign:'text-bottom', outline:'none', ...style }}
    >
      <AlertTriangle size={size} strokeWidth={2.4}/>
      {tooltip && (
        <span style={{ position:'fixed', left:tooltip.left, top:tooltip.top, transform:'translate(-50%, -100%)', background:'#111827', color:'#fff', borderRadius:6, padding:'7px 9px', fontFamily:'Inter,sans-serif', fontSize:11, fontWeight:700, lineHeight:1.35, minWidth:180, maxWidth:300, boxShadow:'0 8px 18px rgba(17,24,39,0.18)', zIndex:1000, pointerEvents:'none' }}>
          {issues.slice(0, 4).map(issue => (
            <span key={`${issue.type}-${issue.detail}`} style={{ display:'block', marginTop:issue === issues[0] ? 0 : 5 }}>
              <strong>{issue.label || 'Needs review'}:</strong> {issue.detail || 'Review this order before fulfillment.'}
            </span>
          ))}
        </span>
      )}
    </span>
  );
};

const CircleSelectControl = ({ checked = false, mixed = false, disabled = false, label = 'Select order', onChange, style = {} }) => (
  <button
    type="button"
    aria-label={label}
    aria-checked={mixed ? 'mixed' : checked}
    role="checkbox"
    disabled={disabled}
    onClick={event => {
      event.stopPropagation();
      if (!disabled) onChange?.(!checked || mixed);
    }}
    style={{
      width:18,
      height:18,
      borderRadius:'50%',
      border:`1.5px solid ${checked || mixed ? '#1E2433' : '#CBD5E1'}`,
      background:checked || mixed ? '#1E2433' : '#fff',
      color:'#fff',
      padding:0,
      display:'inline-flex',
      alignItems:'center',
      justifyContent:'center',
      cursor:disabled ? 'not-allowed' : 'pointer',
      opacity:disabled ? 0.45 : 1,
      verticalAlign:'middle',
      flexShrink:0,
      ...style,
    }}
  >
    {mixed ? (
      <span aria-hidden="true" style={{ width:8, height:2, borderRadius:2, background:'#fff', display:'block' }}/>
    ) : checked ? (
      <Check size={11} strokeWidth={3}/>
    ) : null}
  </button>
);

const FulfilledToggle = ({ checked = false, saving = false, orderId, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={`Mark order ${orderId} ${checked ? 'not fulfilled' : 'fulfilled'}`}
    title={checked ? 'Fulfilled. Click to mark not fulfilled.' : 'Not fulfilled. Click to mark fulfilled.'}
    disabled={saving}
    onClick={event => {
      event.stopPropagation();
      onToggle?.();
    }}
    style={{
      width:30,
      height:30,
      padding:0,
      border:'none',
      background:'transparent',
      display:'inline-flex',
      alignItems:'center',
      justifyContent:'center',
      cursor:saving ? 'wait' : 'pointer',
      opacity:saving ? 0.55 : 1,
    }}
  >
    <span
      aria-hidden="true"
      style={{
        width:19,
        height:19,
        borderRadius:'50%',
        border:`2px solid ${checked ? '#16A34A' : '#22C55E'}`,
        background:checked ? '#16A34A' : '#fff',
        color:'#fff',
        display:'inline-flex',
        alignItems:'center',
        justifyContent:'center',
        boxSizing:'border-box',
        transition:'background 0.12s, border-color 0.12s',
      }}
    >
      {checked && <Check size={13} strokeWidth={3}/>}
    </span>
  </button>
);

const csvEscapeOrders = value => {
  const s = String(value ?? '');
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const ORDERS_BULK_EXPORT_FIELDS = [
  'order_id', 'source_id', 'source', 'order_date', 'full_name', 'email', 'phone',
  'address_line1', 'city', 'state', 'postal_code', 'country', 'sku',
  'product_name', 'quantity', 'amount', 'currency', 'status',
];

const exportOrdersCsv = orders => {
  const fieldDefs = Object.fromEntries((typeof EXPORT_FIELD_DEFS !== 'undefined' ? EXPORT_FIELD_DEFS : []).map(field => [field.key, field]));
  const rows = orders.flatMap(order => orderItemsForDisplay(order).map(item => ({
    ...order,
    item_raw_name:item.raw_name || item.display_name,
    item_sku:item.display_sku || item.sku || item.product_sku || '',
    item_quantity:item.quantity || 1,
    product_name:item.product_name || item.display_name,
    product_sku:item.product_sku || item.display_sku || '',
  })));
  const header = ORDERS_BULK_EXPORT_FIELDS.map(field => csvEscapeOrders(fieldDefs[field]?.label || field)).join(',');
  const body = rows.map(row => ORDERS_BULK_EXPORT_FIELDS.map(field => csvEscapeOrders(fieldDefs[field]?.value(row) ?? '')).join(','));
  const blob = new Blob([`\uFEFF${[header, ...body].join('\n')}`], { type:'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `omnifill-selected-orders-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const DuplicateReviewMarker = ({ size = 13, style = {} }) => {
  const [tooltip, setTooltip] = useState(null);
  const showTooltip = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({ left: rect.left + rect.width / 2, top: rect.top - 8 });
  };

  return (
    <span
      aria-label="Possible duplicate order"
      onMouseEnter={showTooltip}
      onFocus={showTooltip}
      onMouseLeave={() => setTooltip(null)}
      onBlur={() => setTooltip(null)}
      tabIndex={0}
      style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#B45309', lineHeight:1, flexShrink:0, verticalAlign:'text-bottom', outline:'none', ...style }}
    >
      <AlertTriangle size={size} strokeWidth={2.3}/>
      {tooltip && (
        <span style={{ position:'fixed', left:tooltip.left, top:tooltip.top, transform:'translate(-50%, -100%)', background:'#111827', color:'#fff', borderRadius:6, padding:'5px 8px', fontFamily:'Inter,sans-serif', fontSize:11, fontWeight:700, lineHeight:1.2, whiteSpace:'nowrap', boxShadow:'0 8px 18px rgba(17,24,39,0.18)', zIndex:1000, pointerEvents:'none' }}>
          Possible duplicate order
        </span>
      )}
    </span>
  );
};

const orderLocationParts = order => {
  const city = cleanCity(order.city).split(',')[0].trim();
  return [city].filter(Boolean);
};

const CountryFlag = ({ country, size = 14 }) => (
  country?.length === 2
    ? <img src={`/assets/circle-flags/flags/${country.toLowerCase()}.svg`} alt={country} style={{ width:size, height:size, flexShrink:0 }}/>
    : null
);

let mapsConfigPromise = null;
const fetchMapsConfig = () => {
  if (!mapsConfigPromise) {
    mapsConfigPromise = api('GET', '/api/maps-config')
      .then(({ ok, data }) => (ok && data ? data : { browserApiKey:'', addressCheckConfigured:false }))
      .catch(() => ({ browserApiKey:'', addressCheckConfigured:false }));
  }
  return mapsConfigPromise;
};

const ADDRESS_CHECK_META = {
  valid: { label:'Address verified', color:'#166534', Icon:CheckCircle2 },
  review: { label:'Confirm shipping address', color:'#B45309', Icon:AlertTriangle },
  invalid: { label:'Invalid shipping address', color:'#B45309', Icon:AlertTriangle },
  unavailable: { label:'Address check unavailable', color:'#6B7280', Icon:AlertTriangle },
};

const OrderAddressVerification = ({ order, onLocalUpdate, onSuggestedApplied, onEditAddress, hasUnsavedAddressChanges = false }) => {
  const [config, setConfig] = useState(null);
  const [check, setCheck] = useState(() => ({
    status: order.address_check_status || '',
    reason: order.address_check_reason || '',
    formatted: order.address_check_formatted || '',
    lat: order.address_check_lat,
    lng: order.address_check_lng,
    action: order.address_check_action || '',
    suggestionAvailable: !!order.address_check_suggestion_available,
    confirmable: !!order.address_check_confirmable,
  }));
  const [checking, setChecking] = useState(false);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [applyMessage, setApplyMessage] = useState('');
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerInstance = useRef(null);

  useEffect(() => {
    let active = true;
    fetchMapsConfig().then(cfg => { if (active) setConfig(cfg); });
    return () => { active = false; };
  }, []);

  const lat = Number(check.lat);
  const lng = Number(check.lng);
  const hasPoint = Number.isFinite(lat) && Number.isFinite(lng);
  const showMap = hasPoint && !!config?.browserApiKey;
  const addressText = [order.address_line1, order.city, order.state, order.postal_code, order.country]
    .map(value => String(value || '').trim()).filter(Boolean).join(', ');
  const mapsLink = hasPoint
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`
    : addressText
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressText)}`
      : '';

  useEffect(() => {
    if (!showMap || !mapRef.current) return;
    let active = true;
    loadGoogleMaps(config.browserApiKey).then(maps => {
      if (!active || !mapRef.current) return;
      const center = { lat, lng };
      if (!mapInstance.current) {
        mapInstance.current = new maps.Map(mapRef.current, {
          center,
          zoom: check.status === 'valid' ? 16 : 11,
          mapTypeControl:false,
          streetViewControl:false,
          fullscreenControl:false,
        });
      } else {
        mapInstance.current.setCenter(center);
      }
      if (markerInstance.current) markerInstance.current.setMap(null);
      markerInstance.current = new maps.Marker({
        map: mapInstance.current,
        position: center,
        title: check.formatted || addressText || 'Shipping address',
      });
    }).catch(() => {});
    return () => { active = false; };
  }, [showMap, lat, lng, config?.browserApiKey]);

  const runCheck = async () => {
    setChecking(true);
    setCheckError('');
    setApplyMessage('');
    const { ok, status, data } = await api('POST', `/api/orders/${encodeURIComponent(order.id)}/validate-address`, {});
    setChecking(false);
    if (!ok) {
      setCheckError(data?.error || (status === 503 ? 'Address checks are not configured.' : 'Address check failed.'));
      return;
    }
    const next = data?.addressCheck || {};
    setCheck({
      status: next.status || '',
      reason: next.reason || '',
      formatted: next.formatted || '',
      lat: next.lat,
      lng: next.lng,
      action: next.action || '',
      suggestionAvailable: !!next.suggestionAvailable,
      confirmable: !!next.confirmable,
    });
    if (onLocalUpdate) onLocalUpdate(next);
  };

  const submitAddressAction = async (body = {}) => {
    setApplyingSuggestion(true);
    setCheckError('');
    setApplyMessage('');
    const { ok, data } = await api('POST', `/api/orders/${encodeURIComponent(order.id)}/apply-suggested-address`, body);
    setApplyingSuggestion(false);
    if (!ok) {
      setCheckError(data?.error || 'The suggested address could not be applied.');
      return;
    }
    const updatedOrder = data?.order;
    const next = data?.addressCheck || {};
    if (next.status) {
      setCheck({
        status: next.status || '',
        reason: next.reason || '',
        formatted: next.formatted || '',
        lat: next.lat,
        lng: next.lng,
        action: next.action || '',
        suggestionAvailable: !!next.suggestionAvailable,
        confirmable: !!next.confirmable,
      });
    }
    const addressFields = [
      ['address_line1', 'street'],
      ['city', 'city'],
      ['state', 'state / province'],
      ['postal_code', 'postal code'],
      ['country', 'country'],
    ];
    const changedLabels = updatedOrder
      ? addressFields
          .filter(([key]) => String(updatedOrder[key] || '').trim() !== String(order[key] || '').trim())
          .map(([, label]) => label)
      : [];
    setApplyMessage(next.action === 'accept_confirmed'
      ? 'Address confirmed. This order no longer needs address review.'
      : changedLabels.length
        ? `Updated ${changedLabels.join(', ')}. The saved shipping address now appears above.`
        : 'The suggested address matched the saved fields, so no address values changed.');
    if (updatedOrder && onSuggestedApplied) onSuggestedApplied(updatedOrder, next);
  };

  const confirmCurrentAddress = () => {
    if (!confirm('Confirm the current customer-provided shipping address? Only do this after checking it against the original order source.')) return;
    submitAddressAction({ keepCurrentAddress:true });
  };

  const meta = ADDRESS_CHECK_META[check.status] || null;
  const Icon = meta?.Icon || MapIcon;
  const detail = check.status === 'valid'
    ? (check.suggestionAvailable ? 'The saved address is verified. Optional local formatting is available.' : (check.formatted || ''))
    : (check.status ? (check.reason || '') : (config && !config.addressCheckConfigured ? 'Address checks are not configured.' : 'Not checked yet.'));
  const canUseSuggestion = ['valid', 'invalid', 'review'].includes(check.status) && check.suggestionAvailable;
  const canConfirmAddress = ['review', 'invalid'].includes(check.status) && check.confirmable && !check.suggestionAvailable;
  const requiresAddressEdit = check.status === 'review' && check.action === 'add_subpremise';

  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:10, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.1em', marginBottom:8 }}>ADDRESS CHECK</div>
      <div style={{ border:'1px solid #E5E7EB', borderRadius:9, overflow:'hidden' }}>
        <div style={{ padding:'10px 11px 8px' }}>
          <div style={{ display:'flex', gap:8, alignItems:'flex-start', minWidth:0 }}>
            <Icon size={15} color={meta?.color || '#9CA3AF'} strokeWidth={2.3} style={{ flexShrink:0, marginTop:1 }}/>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:800, color:meta?.color || '#6B7280', lineHeight:1.3 }}>{meta?.label || 'Not checked yet'}</div>
              {(detail || checkError) && (
                <div style={{ fontSize:11, color:checkError ? '#B91C1C' : '#6B7280', marginTop:2, lineHeight:1.35, wordBreak:'break-word' }}>
                  {checkError || detail}
                </div>
              )}
              {!checkError && canUseSuggestion && !requiresAddressEdit && check.formatted && (
                <div style={{ fontSize:11, color:'#374151', marginTop:3, lineHeight:1.35, wordBreak:'break-word' }}>
                  <span style={{ fontWeight:800 }}>Google suggests:</span> {check.formatted}
                </div>
              )}
              {applyMessage && (
                <div role="status" style={{ fontSize:11, color:check.status === 'valid' ? '#166534' : '#374151', marginTop:4, lineHeight:1.35, fontWeight:700 }}>
                  {applyMessage}
                </div>
              )}
              {hasUnsavedAddressChanges && (
                <div role="status" style={{ fontSize:11, color:'#B45309', marginTop:4, lineHeight:1.35, fontWeight:700 }}>
                  Save the address edits first. The address check will then refresh using those saved fields.
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center', justifyContent:'center', flexWrap:'wrap', padding:'7px 10px', borderTop:'1px solid #F3F4F6', background:'#FAFAFA' }}>
            {!checkError && !hasUnsavedAddressChanges && !requiresAddressEdit && (canUseSuggestion || canConfirmAddress) && (
              <button onClick={() => submitAddressAction(canConfirmAddress ? { keepCurrentAddress:true } : {})} disabled={applyingSuggestion || checking} style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#006FDD', color:'#fff', border:'1px solid #006FDD', borderRadius:7, padding:'6px 9px', fontSize:12, fontWeight:800, cursor:applyingSuggestion || checking ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
                <Check size={12} strokeWidth={2.5}/>{applyingSuggestion ? (canConfirmAddress ? 'Confirming...' : 'Applying...') : canConfirmAddress ? 'Confirm address' : 'Use suggested address'}
              </button>
            )}
            {!checkError && !hasUnsavedAddressChanges && requiresAddressEdit && onEditAddress && (
              <>
                <button onClick={onEditAddress} disabled={applyingSuggestion} style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#006FDD', color:'#fff', border:'1px solid #006FDD', borderRadius:7, padding:'6px 9px', fontSize:12, fontWeight:800, cursor:applyingSuggestion ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
                  <Edit3 size={12} strokeWidth={2.3}/>Add unit, lot, or suite
                </button>
                <button onClick={confirmCurrentAddress} disabled={applyingSuggestion || checking} style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:7, padding:'6px 9px', fontSize:12, fontWeight:800, cursor:applyingSuggestion || checking ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
                  <Check size={12} strokeWidth={2.3}/>{applyingSuggestion ? 'Confirming...' : 'Confirm without unit'}
                </button>
              </>
            )}
            {mapsLink && (
              <a href={mapsLink} target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:7, padding:'6px 9px', fontSize:12, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>
                <ExternalLink size={12} strokeWidth={2.2}/>Open map
              </a>
            )}
            {config?.addressCheckConfigured && !hasUnsavedAddressChanges && (
              <button onClick={runCheck} disabled={checking} style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:7, padding:'6px 9px', fontSize:12, fontWeight:700, cursor:checking ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
                <RefreshCw size={12} strokeWidth={2.2}/>{checking ? 'Checking...' : check.status ? 'Re-check' : 'Check address'}
              </button>
            )}
        </div>
        {showMap && (
          <div ref={mapRef} style={{ height:150, borderTop:'1px solid #F3F4F6' }}/>
        )}
      </div>
    </div>
  );
};

const OrderDrawer = ({ order, onClose, showRaw, onUpdate, onChanged, dateFormat, onDefine, initialEditing = false }) => {
  const [status, setStatus] = useState(normalizeDashboardOrderStatus(order.status));
  const initialStatusRef = useRef(normalizeDashboardOrderStatus(order.status));
  const [editing, setEditing] = useState(initialEditing);
  const [focusedEditField, setFocusedEditField] = useState(null);
  const editFieldRefs = useRef({});
  const [itemEdits, setItemEdits] = useState(() => {
    const map = {};
    orderItemsForDisplay(order).forEach(item => {
      if (item.id) map[item.id] = { quantity: item.quantity, amount: item.amount ?? '' };
    });
    return map;
  });
  const initialItemEditsRef = useRef(itemEdits);
  const [form, setForm] = useState(() => ({
    date: order.date || '',
    first_name: order.first_name || '',
    last_name: order.last_name || '',
    email: order.email || '',
	    phone: order.phone || '',
	    address_line1: order.address_line1 || '',
	    city: order.city || '',
	    state: order.state || '',
	    postal_code: order.postal_code || '',
	    country: order.country || '',
    product: normalizeProductName(order.product) || '',
    qty: order.qty || 1,
    amount: order.amount || '',
    currency: order.currency || 'usd',
  }));
  const initialFormRef = useRef(form);
	  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [removedItemIds, setRemovedItemIds] = useState(() => new Set());
  const [closeAttentionKey, setCloseAttentionKey] = useState(0);

  useEffect(() => {
    if (!editing || !focusedEditField) return;
    const input = editFieldRefs.current[focusedEditField];
    if (!input) return;
    input.focus();
    if (input.type !== 'date') input.select();
    setFocusedEditField(null);
  }, [editing, focusedEditField]);

  if (!order) return null;

	  const fullName = [form.first_name, form.last_name].filter(Boolean).join(' ');
		  const orderDateLabel = formatDate(order.date, dateFormat);
		  const edited = isEditedOrder(order);
		  const duplicateReview = isDuplicateReviewOrder(order);
		  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
		  const updateItemField = (itemId, key, value) => setItemEdits(prev => ({ ...prev, [itemId]: { ...prev[itemId], [key]: value } }));
		  const originalOrderItems = orderItemsForDisplay(order);
		  const orderItems = originalOrderItems.filter(item => !removedItemIds.has(String(item.id)));
		  const deleteItem = item => {
		    if (!item.id || orderItems.length <= 1) return;
		    setRemovedItemIds(prev => new Set(prev).add(String(item.id)));
		  };
		  const hasFormChanges = Object.entries(form).some(([key, value]) => String(value ?? '') !== String(initialFormRef.current[key] ?? ''));
		  const hasItemChanges = originalOrderItems.some(item => {
		    if (!item.id) return false;
		    const edit = itemEdits[item.id];
		    const initialEdit = initialItemEditsRef.current[item.id];
		    if (!edit || !initialEdit) return false;
		    return String(edit.quantity ?? '') !== String(initialEdit.quantity ?? '')
		      || String(edit.amount ?? '') !== String(initialEdit.amount ?? '');
		  });
		  const hasUnsavedChanges = hasFormChanges
		    || hasItemChanges
		    || removedItemIds.size > 0
		    || status !== initialStatusRef.current;
		  const requestClose = () => {
		    if (hasUnsavedChanges) {
		      setCloseAttentionKey(key => key + 1);
		      return;
		    }
		    onClose();
		  };
		  const drawerOrder = { ...order, status };
		  const hasUnsavedAddressChanges = ['address_line1', 'city', 'state', 'postal_code', 'country']
		    .some(key => String(form[key] || '').trim() !== String(order[key] || '').trim());
		  const reviewIssues = orderReviewIssues(drawerOrder);
		  const fulfillmentStatus = fulfillmentStatusForOrder(drawerOrder, reviewIssues);
		  const statusEditOptions = status === 'routed'
		    ? [
		        { value:'paid', label:'Route by rules', colors:['#64748B'] },
		        { value:'routed', label:'Routed', colors:['#16A34A'] },
		        { value:'problem', label:'Needs review', colors:['#A16207'] },
		        { value:'ignored', label:'Ignored', colors:['#B6BDC8'] },
		      ]
		    : ORDER_STATUS_EDIT_OPTIONS;
		  const editField = key => {
		    if (editing) return;
		    setFocusedEditField(key);
		    setEditing(true);
		  };

  const save = async () => {
    setSaving(true);
    const payload = editing ? { ...form, status } : { status };
    const { ok, data } = await api('PATCH', `/api/orders/${encodeURIComponent(order.id)}`, payload);
    let itemsOk = true;
    if (ok && editing) {
      const updateResults = await Promise.all(Object.entries(itemEdits)
        .filter(([itemId]) => !removedItemIds.has(String(itemId)))
        .map(([itemId, edit]) =>
          api('PATCH', `/api/order-items/${encodeURIComponent(itemId)}`, { quantity: edit.quantity, amount: edit.amount })
        ));
      const deleteResults = await Promise.all([...removedItemIds]
        .map(itemId => api('DELETE', `/api/order-items/${encodeURIComponent(itemId)}`)));
      itemsOk = [...updateResults, ...deleteResults].every(result => result.ok);
    }
    if (ok) {
      const localPatch = data?.order || { ...payload };
      if ('qty' in localPatch) localPatch.qty = Number(localPatch.qty) || 1;
      if (!data?.order && duplicateReview && status !== 'problem') localPatch.duplicate_review_status = status === 'ignored' ? 'ignored' : 'cleared';
      if (onUpdate) onUpdate(order.id, localPatch);
      if (onChanged) onChanged();
    }
    setSaving(false);
    if (ok && itemsOk) onClose();
  };

  const discardChanges = () => {
    if (saving) return;
    onClose();
  };

  const revertEdits = async () => {
    if (!confirm(`Revert all edits for order ${order.id}? This restores the saved source values.`)) return;
    setReverting(true);
    const { ok, data } = await api('POST', `/api/orders/${encodeURIComponent(order.id)}/revert`, {});
    setReverting(false);
    if (ok) {
      if (data?.order && onUpdate) onUpdate(order.id, data.order);
      if (onChanged) onChanged();
      onClose();
    }
  };

  const fields = [
    ['Date', 'date'],
    ['First name', 'first_name'],
    ['Last name', 'last_name'],
    ['Email', 'email'],
    ['Phone', 'phone'],
	    ['Address', 'address_line1'],
	    ['City', 'city'],
	    ['State / Province', 'state'],
	    ['Postal code', 'postal_code'],
	    ['Country', 'country'],
	    ['Amount', 'amount'],
	    ['Currency', 'currency'],
	  ];
  const inputStyle = { width:'100%', border:'1px solid #E5E7EB', borderRadius:7, padding:'7px 9px', fontSize:13, outline:'none', background:'#fff', color:'#1A1A2E' };
  const missing = value => value === null || value === undefined || String(value).trim() === '';
  const displayValue = key => {
    if (key === 'city') return cleanCity(form.city);
	    if (key === 'amount') return form.amount ? `${form.amount} ${(form.currency||'').toUpperCase()}` : '';
	    return form[key] || '';
	  };
  const displayText = value => missing(value) ? 'N/A' : value;
  const mutedValueStyle = isMissing => ({ fontSize:13, fontWeight:isMissing ? 600 : 700, color:isMissing ? '#9CA3AF' : '#111827', wordBreak:'break-word', minWidth:0 });

  return (
    <>
      <div onClick={requestClose} style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.38)', zIndex:40 }}></div>
      <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:16, pointerEvents:'none' }}>
      <div style={{ width:'min(720px, calc(100vw - 32px))', maxHeight:'min(760px, calc(100vh - 104px))', background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, boxShadow:'0 24px 80px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column', overflow:'hidden', pointerEvents:'auto' }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14, flexShrink:0 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
	              <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:16, letterSpacing:'-0.5px' }}>{order.id}</span>
	              {edited && <EditedOrderMarker size={14}/>}
	              <ReviewIssueMarker issues={reviewIssues} size={15}/>
	            </div>
            <div style={{ fontSize:12, color:orderDateLabel ? '#9CA3AF' : '#B6BDC8', marginTop:2, fontWeight:orderDateLabel ? 400 : 600 }}>{orderDateLabel || 'N/A'}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={() => editing && hasUnsavedChanges ? setCloseAttentionKey(key => key + 1) : setEditing(v => !v)} title={editing ? 'View order' : 'Edit order'} style={{ background:editing ? '#EEF6FF' : '#fff', color:editing ? '#006FDD' : '#374151', border:'1px solid #E5E7EB', borderRadius:7, padding:7, cursor:'pointer', display:'flex' }}>
              <Edit3 size={14} strokeWidth={2}/>
            </button>
            <button onClick={requestClose} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#9CA3AF', lineHeight:1, padding:'0 2px' }}>×</button>
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>
          <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
            <PlatformPill source={order.source} size="lg"/>
	            {orderItems.find(item => item.product_image_data) && <ProductThumb src={orderItems.find(item => item.product_image_data)?.product_image_data} size={40}/>}
	            <div style={{ minWidth:0, flex:'1 1 220px' }}>
	              <div style={{ fontWeight:700, fontSize:14, marginBottom:4, color:fullName ? '#111827' : '#9CA3AF', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fullName || 'N/A'}</div>
	              <div style={{ display:'flex', gap:7, alignItems:'center', flexWrap:'wrap' }}>
	                <FulfillmentStatusBadge status={fulfillmentStatus}/>
	              </div>
	            </div>
            <div style={{ fontSize:12, color:form.email ? '#6B7280' : '#9CA3AF', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:260 }}>{form.email || 'N/A'}</div>
	          </div>

		          {reviewIssues.length > 0 && (
		            <div style={{ marginBottom:16, border:'1px solid #E5E7EB', background:'#F8FAFC', borderRadius:8, padding:'10px 12px', display:'grid', gridTemplateColumns:'18px minmax(0, 1fr)', gap:9, alignItems:'start' }}>
		              <AlertTriangle size={16} color="#B45309" strokeWidth={2.3}/>
		              <div style={{ minWidth:0 }}>
		                <div style={{ fontSize:12, fontWeight:900, color:'#374151', marginBottom:5 }}>Needs review</div>
		                <div style={{ display:'grid', gap:5 }}>
		                  {reviewIssues.map(issue => (
		                    <div key={`${issue.type}-${issue.detail}`} style={{ fontSize:12, color:'#4B5563', lineHeight:1.45 }}>
		                      <strong>{issue.label || 'Review'}:</strong> {issue.detail || 'Review this order before fulfillment.'}
		                    </div>
		                  ))}
		                </div>
		              </div>
		            </div>
		          )}

	          <div style={{ marginBottom:16 }}>
	            <div style={{ fontSize:10, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.1em', marginBottom:8 }}>ITEMS</div>
	            <div style={{ border:'1px solid #E5E7EB', borderRadius:9, overflow:'hidden' }}>
	              {orderItems.map((item, index) => (
	                <div key={item.id || index} style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) auto', gap:12, alignItems:'center', padding:'9px 11px', borderTop:index ? '1px solid #F3F4F6' : 'none', background:index % 2 ? '#FAFAFA' : '#fff' }}>
	                  <div style={{ display:'flex', alignItems:'center', gap:9, minWidth:0 }}>
	                    <ProductThumb src={item.product_image_data} size={30}/>
	                    <div style={{ minWidth:0 }}>
	                      <div style={{ fontSize:13, fontWeight:800, color:item.product_id ? '#111827' : '#B45309', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.display_name}</div>
	                      <div style={{ fontSize:11, color:'#9CA3AF', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.display_sku || item.raw_name || 'No SKU'}</div>
	                    </div>
	                  </div>
	                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
	                    {editing && item.id ? (
	                      <>
	                        <input
	                          type="number" min="1" title="Quantity"
	                          value={itemEdits[item.id]?.quantity ?? item.quantity}
	                          onChange={e => updateItemField(item.id, 'quantity', e.target.value)}
	                          style={{ width:48, border:'1px solid #E5E7EB', borderRadius:7, padding:'5px 6px', fontSize:12, outline:'none', background:'#fff', color:'#1A1A2E', fontFamily:'monospace' }}
	                        />
	                        <input
	                          type="text" inputMode="decimal" title="Amount" placeholder="Amount"
	                          value={itemEdits[item.id]?.amount ?? ''}
	                          onChange={e => updateItemField(item.id, 'amount', e.target.value)}
	                          style={{ width:70, border:'1px solid #E5E7EB', borderRadius:7, padding:'5px 6px', fontSize:12, outline:'none', background:'#fff', color:'#1A1A2E', fontFamily:'monospace' }}
	                        />
	                        <span style={{ fontSize:10, color:'#9CA3AF', fontWeight:700 }}>{(item.currency || form.currency || '').toUpperCase()}</span>
	                      </>
	                    ) : (
	                      <span style={{ fontSize:12, fontWeight:900, color:'#374151', fontFamily:'monospace' }}>x{item.quantity}</span>
	                    )}
	                    {!editing && !item.product_id && <span style={{ fontSize:11, fontWeight:800, color:'#B45309', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:999, padding:'3px 7px' }}>Unassigned</span>}
	                    {editing && onDefine && (
	                      <button onClick={() => onDefine({ order, item })} title="Change product" style={{ background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:7, padding:5, cursor:'pointer', display:'flex', flexShrink:0 }}>
	                        <Edit3 size={12} strokeWidth={2}/>
	                      </button>
	                    )}
	                    {editing && item.id && (
	                      <button
	                        onClick={() => deleteItem(item)}
	                        disabled={orderItems.length <= 1}
	                        title={orderItems.length <= 1 ? 'An order needs at least one item' : 'Remove item'}
	                        style={{ background:'#fff', color: orderItems.length <= 1 ? '#D1D5DB' : '#DC2626', border:'1px solid #E5E7EB', borderRadius:7, padding:5, cursor: orderItems.length <= 1 ? 'not-allowed' : 'pointer', display:'flex', flexShrink:0 }}
	                      >
	                        <Trash2 size={12} strokeWidth={2}/>
	                      </button>
	                    )}
	                  </div>
	                </div>
	              ))}
	            </div>
	          </div>

	          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.1em', marginBottom:8 }}>CUSTOMER & SHIPPING</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'8px 12px' }}>
            {fields.map(([k,key]) => {
              const value = displayValue(key);
              const isMissing = missing(value);
              return (
              <div
                key={k}
                onDoubleClick={() => editField(key)}
                title={editing ? undefined : `Double-click to edit ${k.toLowerCase()}`}
                style={{ display:'grid', gridTemplateColumns:'86px minmax(0, 1fr)', gap:8, alignItems:'center', minHeight:34, padding:'5px 0', borderBottom:'1px solid #F3F4F6', cursor:editing ? 'default' : 'text' }}
              >
                <span style={{ fontSize:11, color:'#9CA3AF', whiteSpace:'nowrap' }}>{k}</span>
                {editing ? (
                  <input
                    ref={node => {
                      if (node) editFieldRefs.current[key] = node;
                      else delete editFieldRefs.current[key];
                    }}
                    type={key === 'qty' ? 'number' : key === 'date' ? 'date' : 'text'}
                    min={key === 'qty' ? 1 : undefined}
                    value={form[key] ?? ''}
                    onChange={e => updateField(key, e.target.value)}
                    style={inputStyle}
                  />
                ) : key === 'phone' && !isMissing ? (
                  (() => {
                    const { code, rest } = splitPhoneByCountry(value, form.country);
                    return (
                      <span style={{ display:'inline-flex', alignItems:'center', gap:6, minWidth:0 }}>
                        <CountryFlag country={form.country}/>
                        {code && <span style={{ fontSize:13, fontWeight:700, color:'#4B5563' }}>{code}</span>}
                        <span style={mutedValueStyle(false)}>{rest || value}</span>
                      </span>
                    );
                  })()
                ) : key === 'country' && !isMissing ? (
                  <span style={{ display:'inline-flex', alignItems:'center', gap:6, minWidth:0 }}>
                    <CountryFlag country={value}/>
                    <span style={mutedValueStyle(false)}>{displayText(value)}</span>
                  </span>
                ) : (
                  <span style={mutedValueStyle(isMissing)}>{displayText(value)}</span>
                )}
              </div>
              );
            })}
            </div>
          </div>

          <OrderAddressVerification
            order={drawerOrder}
            hasUnsavedAddressChanges={hasUnsavedAddressChanges}
            onEditAddress={() => setEditing(true)}
            onLocalUpdate={next => {
              if (onUpdate) onUpdate(order.id, {
                address_check_status: next.status || null,
                address_check_reason: next.reason || null,
                address_check_formatted: next.formatted || null,
                address_check_lat: next.lat ?? null,
                address_check_lng: next.lng ?? null,
                address_check_action: next.action || null,
                address_check_suggestion_available: next.suggestionAvailable ? 1 : 0,
                address_check_confirmable: next.confirmable ? 1 : 0,
                address_checked_at: next.checkedAt || null,
                review_issues: (Array.isArray(order.review_issues) ? order.review_issues : [])
                  .filter(issue => issue?.type !== 'address_invalid'),
              });
            }}
            onSuggestedApplied={(updatedOrder, nextCheck) => {
              const savedAddress = {
                address_line1: updatedOrder.address_line1 || '',
                city: updatedOrder.city || '',
                state: updatedOrder.state || '',
                postal_code: updatedOrder.postal_code || '',
                country: updatedOrder.country || '',
              };
              initialFormRef.current = { ...initialFormRef.current, ...savedAddress };
              setForm(prev => ({
                ...prev,
                ...savedAddress,
              }));
              if (onUpdate) onUpdate(order.id, updatedOrder);
              if (onChanged) onChanged();
            }}
          />

          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.1em', marginBottom:8 }}>SOURCE ID</div>
            <div style={{ background:'#F9FAFB', borderRadius:8, padding:'9px 11px', fontFamily:'monospace', fontSize:11, color:(order.source_id || order.stripe_id) ? '#374151' : '#9CA3AF', border:'1px solid #F3F4F6', maxWidth:'100%', whiteSpace:'normal', overflowWrap:'anywhere', wordBreak:'break-word', lineHeight:1.45, fontWeight:(order.source_id || order.stripe_id) ? 600 : 700 }}>
              {order.source_id || order.stripe_id || 'N/A'}
            </div>
          </div>

          <div>
            <div style={{ marginBottom:10 }}>
              <div>
                <div style={{ fontSize:10, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.1em', marginBottom:8 }}>ORDER STATE</div>
                <ColorSelect value={status} onChange={setStatus} options={statusEditOptions} style={{ width:'100%' }} triggerPadding="8px 11px"/>
              </div>
            </div>
            {edited && (
              <button onClick={revertEdits} disabled={reverting || saving} style={{ width:'100%', marginTop:8, background:'#F0F6FF', color:'#005BB8', border:'1px solid #BFDBFE', borderRadius:8, padding:9, fontSize:13, fontWeight:800, cursor: reverting || saving ? 'not-allowed' : 'pointer' }}>
                {reverting ? 'Reverting...' : 'Revert edits'}
              </button>
            )}
          </div>
        </div>
      </div>
      <div style={{ width:'min(720px, calc(100vw - 32px))', height:48, marginTop:8, display:'flex', justifyContent:'flex-end', alignItems:'center', pointerEvents:hasUnsavedChanges ? 'auto' : 'none' }}>
        {hasUnsavedChanges && (
          <div key={closeAttentionKey} className={closeAttentionKey ? 'order-unsaved-actions-attention' : ''} style={{ display:'flex', alignItems:'center', gap:8, padding:6, border:'1px solid #E5E7EB', borderRadius:10, background:'#fff', boxShadow:'0 12px 34px rgba(17,24,39,0.18)' }}>
            <button onClick={discardChanges} disabled={saving} style={{ background:'#fff', color:'#4B5563', border:'1px solid #D1D5DB', borderRadius:7, padding:'8px 12px', fontSize:13, fontWeight:700, cursor:saving ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
              Discard changes
            </button>
            <button onClick={save} disabled={saving} style={{ minWidth:112, background:saving ? '#64748B' : '#1E2433', color:'#fff', border:'none', borderRadius:7, padding:'9px 13px', fontSize:13, fontWeight:700, cursor:saving ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

const MANUAL_ORDER_INITIAL = {
  date: new Date().toISOString().slice(0, 10),
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  product_id: '',
  qty: 1,
  amount: '',
  currency: 'usd',
  status: 'paid',
};

const MANUAL_CURRENCY_OPTIONS = [
  { value:'usd', label:'$ USD' },
  { value:'eur', label:'€ EUR' },
  { value:'gbp', label:'£ GBP' },
  { value:'cad', label:'C$ CAD' },
  { value:'aud', label:'A$ AUD' },
  { value:'jpy', label:'¥ JPY' },
];

const newAddressSessionToken = () => (
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const addressSuggestionErrorMessage = (status, data) => {
  const raw = String(data?.error || '').trim();
  if (status === 503 || /not configured/i.test(raw)) return '';
  if (/referer|referrer|not allowed|blocked/i.test(raw)) return 'Google Places key is blocked by the dashboard domain restriction.';
  if (/not been used|disabled|not enabled|ApiNotActivated/i.test(raw)) return 'Enable Places API (New) for this Google project.';
  if (/billing/i.test(raw)) return 'Google billing is not enabled for address suggestions.';
  if (/quota|RESOURCE_EXHAUSTED|OVER_QUERY_LIMIT/i.test(raw)) return 'Google Places quota has been reached.';
  if (/denied|PERMISSION_DENIED|REQUEST_DENIED/i.test(raw)) return 'Google denied the address request.';
  return raw ? `Address suggestions are unavailable: ${raw.slice(0, 120)}` : 'Address suggestions are unavailable.';
};

const ManualOrderModal = ({ products = [], onClose, onAdded, onProductsChanged }) => {
  const [form, setForm] = useState(() => ({ ...MANUAL_ORDER_INITIAL }));
  const [manualItems, setManualItems] = useState(() => [{ key: `${Date.now()}-0`, product_id:'', qty:1 }]);
  const [localProducts, setLocalProducts] = useState(products || []);
  const [newProductOpen, setNewProductOpen] = useState(!products?.length);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [addressStatus, setAddressStatus] = useState('');
  const [addressSessionToken, setAddressSessionToken] = useState(() => newAddressSessionToken());
  const addressBlurTimer = useRef(null);
  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setItemField = (key, field, value) => setManualItems(items => items.map(item => item.key === key ? { ...item, [field]: value } : item));
  const addItem = productId => setManualItems(items => [...items, { key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, product_id:productId || '', qty:1 }]);
  const removeItem = key => setManualItems(items => items.length > 1 ? items.filter(item => item.key !== key) : items);
  const inputStyle = { width:'100%', border:'1px solid #E5E7EB', borderRadius:7, padding:'8px 10px', fontSize:13, outline:'none', background:'#fff', color:'#1A1A2E' };
  const labelStyle = { display:'block', fontSize:10, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.08em', marginBottom:6 };
  const requiredFields = new Set(['first_name', 'last_name', 'email']);
  const labelText = (label, required = false) => (
    <>
      {label.toUpperCase()}{required && <span style={{ color:'#DC2626', marginLeft:3 }}>*</span>}
    </>
  );
  const sourceId = () => `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const selectedItems = manualItems.map(item => ({
    ...item,
    product: localProducts.find(p => String(p.id) === String(item.product_id)),
    qty: Math.max(1, Number.parseInt(item.qty, 10) || 1),
  }));

  useEffect(() => {
    setLocalProducts(products || []);
    if (products?.length) setNewProductOpen(false);
  }, [products]);

  useEffect(() => () => {
    if (addressBlurTimer.current) clearTimeout(addressBlurTimer.current);
  }, []);

  useEffect(() => {
    const input = String(form.address_line1 || '').trim();
    if (!addressOpen || input.length < 3) {
      setAddressSuggestions([]);
      setAddressLoading(false);
      setAddressStatus('');
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setAddressLoading(true);
      setAddressStatus('');
      const { ok, data, status } = await api(
        'GET',
        `/api/address/autocomplete?input=${encodeURIComponent(input)}&sessionToken=${encodeURIComponent(addressSessionToken)}`,
        undefined,
        { signal: controller.signal },
      ).catch(error => {
        if (error.name === 'AbortError') return { ok:false, aborted:true };
        return { ok:false, data:{} };
      });
      if (controller.signal.aborted) return;
      setAddressLoading(false);
      if (!ok) {
        setAddressSuggestions([]);
        setAddressStatus(addressSuggestionErrorMessage(status, data));
        return;
      }
      setAddressSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
    }, 240);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.address_line1, addressOpen, addressSessionToken]);

  const chooseAddressSuggestion = async suggestion => {
    if (!suggestion?.placeId) return;
    setAddressLoading(true);
    setAddressStatus('');
    const { ok, data } = await api(
      'GET',
      `/api/address/place/${encodeURIComponent(suggestion.placeId)}?sessionToken=${encodeURIComponent(addressSessionToken)}`,
    );
    setAddressLoading(false);
    setAddressOpen(false);
    setAddressSuggestions([]);
    setAddressSessionToken(newAddressSessionToken());
    if (!ok || !data?.address) {
      setAddressStatus('Could not fill that address.');
      return;
    }
    const address = data.address;
    setForm(f => ({
      ...f,
      address_line1: address.address_line1 || suggestion.text || f.address_line1,
      address_line2: address.address_line2 || f.address_line2,
      city: address.city || f.city,
      state: address.state || f.state,
      postal_code: address.postal_code || f.postal_code,
      country: address.country || f.country,
    }));
  };

  const submit = async () => {
    setError('');
    const hasCustomer = form.first_name.trim() || form.last_name.trim() || form.email.trim();
    if (!hasCustomer) { setError('Add a customer name or email.'); return; }
    if (!selectedItems.length || selectedItems.some(item => !item.product)) { setError('Select or create a product for every item.'); return; }
    setSaving(true);
    const parsedAmount = parseFloat(String(form.amount || '').replace(/[^0-9.-]/g, ''));
    const totalQty = selectedItems.reduce((sum, item) => sum + item.qty, 0) || selectedItems.length;
    const firstItem = selectedItems[0];
    const order = {
      ...form,
      source: 'manual',
      source_id: sourceId(),
      product: firstItem.product.name,
      product_id: firstItem.product.id,
      qty: firstItem.qty,
      items: selectedItems.map(item => ({
        raw_name: item.product.name,
        product_id: item.product.id,
        sku: item.product.sku || '',
        quantity: item.qty,
        amount: Number.isFinite(parsedAmount) ? (parsedAmount * item.qty / totalQty).toFixed(2) : null,
        currency: (form.currency || 'usd').trim().toLowerCase(),
      })),
      currency: (form.currency || 'usd').trim().toLowerCase(),
    };
    const { ok, data } = await api('POST', '/api/orders/import', { orders: [order] });
    setSaving(false);
    if (!ok) {
      setError(data?.error || 'Could not add order.');
      return;
    }
    onAdded?.();
  };

  const fields = [
    ['First name', 'first_name', 'text'], ['Last name', 'last_name', 'text'],
    ['Email', 'email', 'email'], ['Phone', 'phone', 'text'],
    ['Address', 'address_line1', 'text'], ['Address 2', 'address_line2', 'text'],
    ['City', 'city', 'text'], ['State / Province', 'state', 'text'], ['Postal code', 'postal_code', 'text'], ['Country', 'country', 'text'],
	    ['Amount', 'amount', 'text'],
	  ];

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.38)', zIndex:40 }}></div>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'min(720px, calc(100vw - 32px))', maxHeight:'min(820px, calc(100vh - 32px))', background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, boxShadow:'0 24px 80px rgba(0,0,0,0.22)', zIndex:50, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:800, fontSize:16 }}>Add manual order</div>
            <div style={{ fontSize:12, color:'#9CA3AF', marginTop:2 }}>Create an order directly in Omnifill.</div>
          </div>
          <button type="button" onClick={onClose} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#9CA3AF', lineHeight:1, padding:'0 2px' }}>×</button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'20px 22px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:14 }}>
	            <div style={{ gridColumn:'1 / -1', border:'1px solid #E5E7EB', borderRadius:10, padding:12, background:'#FAFAFA' }}>
	              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, marginBottom:12 }}>
	                <div>
	                  <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E' }}>Items</div>
	                  <div style={{ fontSize:12, color:'#9CA3AF', marginTop:2 }}>Add every product in this customer order.</div>
	                </div>
	                <button type="button" onClick={() => addItem()} disabled={!localProducts.length} style={{ background:localProducts.length ? '#006FDD' : '#E5E7EB', color:localProducts.length ? '#fff' : '#9CA3AF', border:'none', borderRadius:8, padding:'8px 11px', fontSize:13, fontWeight:800, cursor:localProducts.length ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap' }}>
	                  <Plus size={13} strokeWidth={2.4}/>Add item
	                </button>
	              </div>
	              <div style={{ display:'grid', gap:9 }}>
	                {manualItems.map((item, index) => (
	                  <div key={item.key} style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) 92px 34px', gap:8, alignItems:'end' }}>
	                    <div>
	                      {index === 0 && <label style={labelStyle}>{labelText('Product', true)}</label>}
	                      <select value={item.product_id} onChange={e => setItemField(item.key, 'product_id', e.target.value)} disabled={!localProducts.length} style={{ ...inputStyle, cursor:localProducts.length ? 'pointer' : 'not-allowed', background:'#fff' }}>
	                        {!localProducts.length && <option value="">Create a product first</option>}
	                        {!!localProducts.length && <option value="">Select a product</option>}
	                        {localProducts.map(p => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>)}
	                      </select>
	                    </div>
	                    <div>
	                      {index === 0 && <label style={labelStyle}>QTY</label>}
	                      <input type="number" min="1" value={item.qty} onChange={e => setItemField(item.key, 'qty', e.target.value)} style={inputStyle}/>
	                    </div>
	                    <button type="button" onClick={() => removeItem(item.key)} disabled={manualItems.length === 1} title="Remove item" style={{ width:34, height:34, border:'1px solid #E5E7EB', borderRadius:8, background:manualItems.length === 1 ? '#F9FAFB' : '#fff', color:manualItems.length === 1 ? '#D1D5DB' : '#DC2626', cursor:manualItems.length === 1 ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
	                      <Trash2 size={14} strokeWidth={2.1}/>
	                    </button>
	                  </div>
	                ))}
	              </div>
	              <div style={{ marginTop:12, borderTop:'1px solid #E5E7EB', paddingTop:12 }}>
	                {newProductOpen ? (
	                  <ProductForm
	                    onSaved={product => {
	                      setLocalProducts(list => [product, ...list.filter(p => p.id !== product.id)]);
	                      setManualItems(items => {
	                        const emptyIndex = items.findIndex(item => !item.product_id);
	                        if (emptyIndex === -1) return [...items, { key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, product_id: product.id, qty: 1 }];
	                        return items.map((item, index) => index === emptyIndex ? { ...item, product_id: product.id } : item);
	                      });
	                      setNewProductOpen(false);
	                      onProductsChanged?.();
	                    }}
	                    onCancel={localProducts.length ? () => setNewProductOpen(false) : null}
	                  />
	                ) : (
	                  <button type="button" onClick={() => setNewProductOpen(true)} style={{ background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 11px', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
	                    <Plus size={13} strokeWidth={2.4}/>Create product
	                  </button>
	                )}
	              </div>
	            </div>
            <div>
              <label style={labelStyle}>ORDER DATE</label>
              <input type="date" value={form.date} onChange={e => setField('date', e.target.value)} style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>ORDER STATE</label>
              <select value={form.status} onChange={e => setField('status', e.target.value)} style={{ ...inputStyle, cursor:'pointer' }}>
                <option value="paid">Route by rules</option>
                <option value="problem">Needs review</option>
                <option value="ignored">Ignored</option>
              </select>
            </div>
            {fields.map(([label, key, type]) => (
              <div key={key}>
                <label style={labelStyle}>{labelText(label, requiredFields.has(key))}</label>
                {key === 'amount' ? (
                  <div style={{ display:'flex', width:'100%', border:'1px solid #E5E7EB', borderRadius:7, background:'#fff', overflow:'hidden' }}>
                    <input
                      type={type}
                      value={form[key]}
                      onChange={e => setField(key, e.target.value)}
                      style={{ flex:1, minWidth:0, border:'none', padding:'8px 10px', fontSize:13, outline:'none', background:'#fff', color:'#1A1A2E' }}
                    />
                    <select
                      value={form.currency}
                      onChange={e => setField('currency', e.target.value)}
                      aria-label="Currency"
                      style={{ width:96, border:'none', borderLeft:'1px solid #E5E7EB', padding:'8px 8px', fontSize:12, fontWeight:800, outline:'none', background:'#F9FAFB', color:'#374151', cursor:'pointer' }}
                    >
                      {MANUAL_CURRENCY_OPTIONS.map(currency => (
                        <option key={currency.value} value={currency.value}>{currency.label}</option>
                      ))}
                    </select>
                  </div>
                ) : key === 'address_line1' ? (
                  <div style={{ position:'relative' }}>
                    <input
                      type={type}
                      value={form[key]}
                      autoComplete="street-address"
                      onFocus={() => {
                        if (addressBlurTimer.current) clearTimeout(addressBlurTimer.current);
                        if (String(form.address_line1 || '').trim().length >= 3) setAddressOpen(true);
                      }}
                      onBlur={() => {
                        addressBlurTimer.current = setTimeout(() => setAddressOpen(false), 160);
                      }}
                      onChange={e => {
                        setField(key, e.target.value);
                        setAddressOpen(true);
                      }}
                      style={inputStyle}
                    />
                    {addressOpen && (addressLoading || addressStatus || addressSuggestions.length > 0) && (
                      <div style={{ position:'absolute', top:'calc(100% + 5px)', left:0, right:0, zIndex:70, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 12px 30px rgba(17,24,39,0.14)', overflow:'hidden' }}>
                        {addressLoading && !addressSuggestions.length && (
                          <div style={{ padding:'9px 10px', fontSize:12, color:'#9CA3AF' }}>Searching addresses...</div>
                        )}
                        {addressStatus && (
                          <div style={{ padding:'9px 10px', fontSize:12, color:'#9CA3AF' }}>{addressStatus}</div>
                        )}
                        {addressSuggestions.map(suggestion => (
                          <button
                            key={suggestion.placeId}
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => chooseAddressSuggestion(suggestion)}
                            style={{ width:'100%', border:'none', borderBottom:'1px solid #F3F4F6', background:'#fff', padding:'9px 10px', textAlign:'left', cursor:'pointer', display:'block' }}
                          >
                            <div style={{ fontSize:13, fontWeight:800, color:'#1F2937', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{suggestion.mainText || suggestion.text}</div>
                            {suggestion.secondaryText && <div style={{ marginTop:2, fontSize:11, color:'#6B7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{suggestion.secondaryText}</div>}
                          </button>
                        ))}
                        {addressSuggestions.length > 0 && (
                          <div style={{ padding:'6px 10px', fontSize:10, fontWeight:800, color:'#9CA3AF', textAlign:'right', background:'#F9FAFB' }}>Powered by Google</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    type={type}
                    min={key === 'qty' ? 1 : undefined}
                    value={form[key]}
                    onChange={e => setField(key, e.target.value)}
                    style={inputStyle}
                  />
                )}
              </div>
            ))}
          </div>
          {error && <div style={{ marginTop:14, color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'9px 11px', fontSize:13 }}>{error}</div>}
        </div>
        <div style={{ padding:'14px 22px', borderTop:'1px solid #F3F4F6', display:'flex', justifyContent:'flex-end', gap:10, flexShrink:0 }}>
          <button type="button" onClick={onClose} disabled={saving} style={{ background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 13px', fontSize:13, fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer' }}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} style={{ background: saving ? '#93C5FD' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:800, cursor: saving ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', gap:6 }}>
            <Plus size={14} strokeWidth={2.4}/>{saving ? 'Adding...' : 'Add order'}
          </button>
        </div>
      </div>
    </>
  );
};

const AI_AUDIT_SEVERITY_META = {
  high: { label:'High', color:'#DC2626' },
  medium: { label:'Medium', color:'#D97706' },
  low: { label:'Low', color:'#6B7280' },
};

const OrderAiAuditModal = ({ open, loading, result, error, orderCount, onClose, onOpenOrder }) => {
  if (!open) return null;
  const findings = Array.isArray(result?.findings) ? result.findings : [];
  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.38)', zIndex:30 }}></div>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'min(640px, calc(100vw - 32px))', maxHeight:'min(680px, calc(100vh - 32px))', background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, boxShadow:'0 24px 80px rgba(0,0,0,0.22)', zIndex:35, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <Sparkles size={16} color="#006FDD" strokeWidth={2.2}/>
            <span style={{ fontWeight:800, fontSize:15, color:'#111827' }}>AI order check</span>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#9CA3AF', lineHeight:1, padding:'0 2px' }}>×</button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>
          {loading ? (
            <div style={{ display:'flex', alignItems:'center', gap:10, color:'#4B5563', fontSize:13, fontWeight:600, padding:'18px 0' }}>
              <RefreshCw size={15} strokeWidth={2.2} style={{ animation:'spin 0.8s linear infinite' }}/>
              Analyzing {orderCount} order{orderCount === 1 ? '' : 's'} with AI. This usually takes a few seconds.
            </div>
          ) : error ? (
            <div style={{ color:'#B91C1C', fontSize:13, fontWeight:700, lineHeight:1.5, padding:'12px 0' }}>{error}</div>
          ) : result ? (
            <>
              {result.summary && (
                <div style={{ fontSize:13, color:'#374151', lineHeight:1.55, marginBottom:14 }}>{result.summary}</div>
              )}
              {findings.length === 0 ? (
                <div style={{ display:'flex', alignItems:'center', gap:9, border:'1px solid #E5E7EB', background:'#F8FAFC', borderRadius:8, padding:'12px 14px', color:'#166534', fontSize:13, fontWeight:700 }}>
                  <CheckCircle2 size={16} strokeWidth={2.2}/>
                  No issues worth flagging were found.
                </div>
              ) : (
                <div style={{ display:'grid', gap:10 }}>
                  {findings.map((finding, index) => {
                    const meta = AI_AUDIT_SEVERITY_META[finding.severity] || AI_AUDIT_SEVERITY_META.low;
                    return (
                      <div key={index} style={{ border:'1px solid #E5E7EB', borderRadius:9, padding:'11px 13px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                          <AlertTriangle size={14} color={meta.color} strokeWidth={2.4} style={{ flexShrink:0 }}/>
                          <span style={{ fontSize:13, fontWeight:800, color:'#111827', minWidth:0 }}>{finding.title}</span>
                          <span style={{ fontSize:11, fontWeight:800, color:meta.color, marginLeft:'auto', whiteSpace:'nowrap' }}>{meta.label}</span>
                        </div>
                        <div style={{ fontSize:12.5, color:'#4B5563', lineHeight:1.5 }}>{finding.detail}</div>
                        {finding.orderIds?.length > 0 && (
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
                            {finding.orderIds.map(id => (
                              <button key={id} onClick={() => onOpenOrder(id)} title="Open order" style={{ background:'#F9FAFB', color:'#005BB8', border:'1px solid #E5E7EB', borderRadius:6, padding:'3px 7px', fontSize:11, fontWeight:700, fontFamily:'monospace', cursor:'pointer' }}>
                                {id}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ marginTop:14, fontSize:11, color:'#9CA3AF', fontWeight:600 }}>
                Checked {result.analyzed} order{result.analyzed === 1 ? '' : 's'}{result.truncated ? ' (most recent)' : ''}. AI can miss things or over-flag; use your judgment.
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

const invoiceEscapeHtml = value => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const printableInvoiceHtml = (order, profile, invoiceNumber, invoiceDate, shipment) => {
  const items = shipment.items || [];
  const currency = String(order.currency || 'usd').toUpperCase();
  const recipientName = [order.first_name, order.last_name].filter(Boolean).join(' ') || 'N/A';
  const recipientAddress = [
    order.company,
    order.address_line1,
    order.address_line2,
    [order.city, order.state, order.postal_code].filter(Boolean).join(', '),
    order.country,
  ].filter(Boolean);
  const sellerAddress = String(profile.address || '').split(/\r?\n/).map(invoiceEscapeHtml).join('<br>');
  const dispatchAddress = String(profile.dispatchAddress || '').split(/\r?\n/).map(invoiceEscapeHtml).join('<br>');
  const logo = profile.logoData
    ? `<img src="${invoiceEscapeHtml(profile.logoData)}" alt="" style="display:block;max-width:180px;max-height:72px;object-fit:contain;margin-bottom:14px">`
    : '';
  const itemRows = items.map(item => {
    const quantity = Number(item.quantity || 1) || 1;
    const unitValue = Number(item.unitValue || 0);
    const lineValue = unitValue * quantity;
    const lineNetWeight = Number(item.netWeightKg || 0) * quantity;
    return `
      <tr>
        <td>
          <strong>${invoiceEscapeHtml(item.description)}</strong>
          <div class="muted">${invoiceEscapeHtml(item.name)}${item.sku ? ` · SKU: ${invoiceEscapeHtml(item.sku)}` : ''}</div>
        </td>
        <td>${invoiceEscapeHtml(item.hsCode)}</td>
        <td>${invoiceEscapeHtml(item.origin)}</td>
        <td class="number">${quantity}</td>
        <td class="number">${unitValue.toFixed(2)} ${invoiceEscapeHtml(currency)}</td>
        <td class="number">${lineValue.toFixed(2)} ${invoiceEscapeHtml(currency)}</td>
        <td class="number">${lineNetWeight.toFixed(3)} kg</td>
      </tr>`;
  }).join('');
  const declaredTotal = items.reduce((sum, item) => sum + (Number(item.unitValue || 0) * (Number(item.quantity || 1) || 1)), 0);
  const totalNetWeight = items.reduce((sum, item) => sum + (Number(item.netWeightKg || 0) * (Number(item.quantity || 1) || 1)), 0);
  const grossWeight = Number(shipment.grossWeightKg) > 0 ? Number(shipment.grossWeightKg) : totalNetWeight;
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Commercial invoice ${invoiceEscapeHtml(invoiceNumber)}</title>
  <style>
    @page { size: A4; margin: 18mm; }
    * { box-sizing: border-box; }
    body { margin: 0; color: #172033; font: 11px/1.45 Arial, sans-serif; }
    .top { display: flex; justify-content: space-between; gap: 36px; padding-bottom: 24px; border-bottom: 2px solid #172033; }
    h1 { margin: 0 0 8px; font-size: 30px; letter-spacing: .04em; }
    h2 { margin: 0 0 6px; font-size: 15px; }
    .meta { min-width: 220px; }
    .meta-row { display: flex; justify-content: space-between; gap: 24px; margin: 3px 0; }
    .muted { color: #6b7280; font-size: 11px; }
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 44px; padding: 24px 0; }
    .label { margin-bottom: 6px; color: #6b7280; font-size: 10px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
    table { width: 100%; border-collapse: collapse; }
    th { padding: 8px 6px; background: #f3f4f6; color: #4b5563; font-size: 8px; letter-spacing: .04em; text-align: left; text-transform: uppercase; }
    td { padding: 9px 6px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
    .number { text-align: right; white-space: nowrap; }
    .shipment { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 24px; padding: 0 0 22px; }
    .shipment strong { display: block; margin-top: 2px; }
    .total { display: flex; justify-content: flex-end; padding-top: 18px; }
    .total-box { min-width: 270px; display: flex; justify-content: space-between; gap: 30px; padding: 12px 10px; border-top: 2px solid #172033; font-size: 16px; font-weight: 700; }
    .footer { margin-top: 42px; padding-top: 12px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 10px; }
    @media print { .print-help { display: none; } }
  </style>
</head>
<body>
  <div class="top">
    <div>
      ${logo}
      <h1>COMMERCIAL INVOICE</h1>
      <div>Paid order</div>
    </div>
    <div class="meta">
      <div class="meta-row"><span class="muted">Invoice</span><strong>${invoiceEscapeHtml(invoiceNumber)}</strong></div>
      <div class="meta-row"><span class="muted">Invoice date</span><strong>${invoiceEscapeHtml(invoiceDate)}</strong></div>
      <div class="meta-row"><span class="muted">Order</span><strong>${invoiceEscapeHtml(order.id)}</strong></div>
      ${shipment.waybill ? `<div class="meta-row"><span class="muted">Waybill</span><strong>${invoiceEscapeHtml(shipment.waybill)}</strong></div>` : ''}
    </div>
  </div>
  <div class="parties">
    <div>
      <div class="label">Seller</div>
      <h2>${invoiceEscapeHtml(profile.businessName)}</h2>
      <div>${sellerAddress}</div>
      ${profile.email ? `<div>${invoiceEscapeHtml(profile.email)}</div>` : ''}
      ${profile.phone ? `<div>${invoiceEscapeHtml(profile.phone)}</div>` : ''}
      ${profile.taxId ? `<div>Tax ID: ${invoiceEscapeHtml(profile.taxId)}</div>` : ''}
      ${profile.eori ? `<div>EORI: ${invoiceEscapeHtml(profile.eori)}</div>` : ''}
    </div>
    <div>
      <div class="label">Consignee / ship to</div>
      <h2>${invoiceEscapeHtml(recipientName)}</h2>
      <div>${recipientAddress.map(invoiceEscapeHtml).join('<br>')}</div>
      ${order.email ? `<div>${invoiceEscapeHtml(order.email)}</div>` : ''}
      ${order.phone ? `<div>${invoiceEscapeHtml(order.phone)}</div>` : ''}
    </div>
  </div>
  ${dispatchAddress ? `<div style="margin:-8px 0 22px"><div class="label">Goods dispatched from</div><div>${dispatchAddress}</div></div>` : ''}
  <div class="shipment">
    <div><span class="muted">Reason for export</span><strong>${invoiceEscapeHtml(shipment.reasonExport)}</strong></div>
    <div><span class="muted">Incoterm</span><strong>${invoiceEscapeHtml(commercialInvoiceIncoterm(shipment.incoterm, order.city, order.country))}</strong></div>
    <div><span class="muted">Type of export</span><strong>Permanent</strong></div>
    <div><span class="muted">Number of packages</span><strong>${invoiceEscapeHtml(shipment.packageCount)}</strong></div>
    <div><span class="muted">Total net weight</span><strong>${totalNetWeight.toFixed(3)} kg</strong></div>
    <div><span class="muted">Gross weight</span><strong>${grossWeight.toFixed(3)} kg</strong></div>
  </div>
  <table>
    <thead><tr><th>Description</th><th>HS code</th><th>Origin</th><th class="number">Qty</th><th class="number">Unit value</th><th class="number">Line value</th><th class="number">Net weight</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  <div class="total">
    <div class="total-box"><span>Total declared value</span><span>${declaredTotal.toFixed(2)} ${invoiceEscapeHtml(currency)}</span></div>
  </div>
  ${shipment.freightStatement ? `<div style="text-align:right;margin-top:4px;color:#374151"><strong>Freight:</strong> ${invoiceEscapeHtml(shipment.freightStatement)}</div>` : ''}
  <div class="footer">
    ${profile.vatStatement ? `<div style="margin-bottom:6px;color:#374151"><strong>VAT:</strong> ${invoiceEscapeHtml(profile.vatStatement)}</div>` : ''}
    ${profile.defaultNote ? `<div style="margin-bottom:6px;color:#374151">${invoiceEscapeHtml(profile.defaultNote)}</div>` : ''}
    <div style="margin-bottom:12px;color:#374151">I certify that the information on this invoice is true and correct and that the contents of this shipment are as stated above.</div>
    ${profile.signatureData ? `<img src="${invoiceEscapeHtml(profile.signatureData)}" alt="" style="display:block;max-width:150px;max-height:56px;object-fit:contain;margin:4px 0">` : ''}
    ${profile.signerName ? `<div style="color:#172033;font-weight:700">${invoiceEscapeHtml(profile.signerName)}</div><div>Authorized signature</div>` : ''}
  </div>
  <script>window.addEventListener('load', () => window.print());<\/script>
</body>
</html>`;
};

const InvoiceGeneratorModal = ({ order, onClose, onNavigate }) => {
  const initialDraft = commercialInvoiceDraftForOrder(order);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceNumber, setInvoiceNumber] = useState(initialDraft.invoiceNumber);
  const [invoiceDate, setInvoiceDate] = useState(initialDraft.invoiceDate);
  const [shipment, setShipment] = useState(initialDraft.shipment);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  useEffect(() => {
    let active = true;
    api('GET', '/api/invoice-settings').then(({ ok, data }) => {
      if (!active) return;
      if (!ok) {
        setError(data?.error || 'Could not load invoice settings.');
        setLoading(false);
        return;
      }
      const next = data?.settings || {};
      setProfile(next);
      setInvoiceNumber(`${next.invoicePrefix || 'INV'}-${order.id}`);
      setShipment(prev => ({
        ...prev,
        reasonExport:next.defaultReasonExport || COMMERCIAL_INVOICE_REASON_OPTIONS[0],
        incoterm:next.defaultIncoterm || '',
        freightStatement:next.defaultFreightStatement || '',
      }));
      setLoading(false);
    }).catch(() => {
      if (!active) return;
      setError('Could not load invoice settings.');
      setLoading(false);
    });
    return () => { active = false; };
  }, [order.id]);
  const openInvoiceSettings = () => {
    try { sessionStorage.setItem('omnifill_settings_page', 'invoices'); } catch {}
    onClose();
    onNavigate?.('settings');
  };
  const updateShipment = (key, value) => {
    setShipment(prev => ({ ...prev, [key]:value }));
    setError('');
  };
  const updateShipmentItem = (index, key, value) => {
    setShipment(prev => ({
      ...prev,
      items:prev.items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]:value } : item),
    }));
    setError('');
  };
  const generate = async () => {
    const validation = validateCommercialInvoiceDraft({ profile, order, invoiceNumber, invoiceDate, shipment });
    if (validation.error) {
      setError(validation.error);
      return;
    }
    const invoiceWindow = window.open('', '_blank');
    if (!invoiceWindow) {
      setError('Your browser blocked the invoice window. Allow pop-ups and try again.');
      return;
    }
    invoiceWindow.opener = null;
    invoiceWindow.document.write('<!doctype html><title>Preparing commercial invoice</title><body style="margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;font:14px Arial,sans-serif;color:#475467;background:#f8fafc">Preparing PDF...</body>');
    invoiceWindow.document.close();
    setGenerating(true);
    setError('');
    try {
      const [logoData, signatureData] = await Promise.all([
        invoiceImageForPdf(profile.logoData),
        invoiceImageForPdf(profile.signatureData),
      ]);
      const response = await fetch('/api/commercial-invoices/pdf', {
        method:'POST',
        credentials:'same-origin',
        headers:{ 'Content-Type':'application/json' },
        body:JSON.stringify({
          invoiceNumber:invoiceNumber.trim(),
          invoiceDate,
          profileImages:{ logoData, signatureData },
          order:{
            id:order.id,
            currency:String(order.currency || 'usd').toUpperCase(),
            firstName:order.first_name || '',
            lastName:order.last_name || '',
            company:order.company || '',
            addressLine1:order.address_line1 || '',
            addressLine2:order.address_line2 || '',
            city:order.city || '',
            state:order.state || '',
            postalCode:order.postal_code || '',
            country:order.country || '',
            email:order.email || '',
            phone:order.phone || '',
          },
          shipment:{
            ...shipment,
            grossWeightKg:validation.grossWeightKg,
          },
        }),
      });
      if (!response.ok) {
        let message = 'Could not generate the commercial invoice PDF.';
        try {
          const data = await response.json();
          if (data?.error) message = data.error;
        } catch {}
        throw new Error(message);
      }
      const pdfBlob = await response.blob();
      if (pdfBlob.type !== 'application/pdf') throw new Error('The server did not return a PDF.');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      invoiceWindow.location.replace(pdfUrl);
      onClose();
    } catch (err) {
      if (!invoiceWindow.closed) invoiceWindow.close();
      setError(err?.message || 'Could not generate the commercial invoice PDF.');
    } finally {
      setGenerating(false);
    }
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.48)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10020, padding:18 }} onClick={onClose}>
      <div style={{ width:860, maxWidth:'100%', maxHeight:'calc(100vh - 36px)', overflow:'auto', background:'#fff', border:'1px solid #E5E7EB', borderRadius:14, boxShadow:'0 24px 70px rgba(15,23,42,0.24)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, padding:'18px 20px', borderBottom:'1px solid #F3F4F6' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1A1A2E' }}>Generate invoice</div>
            <div style={{ fontSize:12, color:'#9CA3AF', marginTop:3 }}>Order {order.id}. The invoice opens as an A4 PDF.</div>
          </div>
          <button onClick={onClose} title="Close" style={{ width:32, height:32, border:'1px solid #E5E7EB', borderRadius:8, background:'#F9FAFB', color:'#374151', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><X size={15}/></button>
        </div>
        <div style={{ padding:'18px 20px', display:'grid', gap:13 }}>
          {loading ? (
            <>
              <SkeletonPill width="100%" height={58} style={{ borderRadius:8 }}/>
              <SkeletonPill width="82%" height={88} style={{ borderRadius:8 }}/>
            </>
          ) : (
            <>
              <CommercialInvoiceFields
                order={order}
                invoiceNumber={invoiceNumber}
                invoiceDate={invoiceDate}
                shipment={shipment}
                onInvoiceNumberChange={value => { setInvoiceNumber(value); setError(''); }}
                onInvoiceDateChange={value => { setInvoiceDate(value); setError(''); }}
                onShipmentChange={updateShipment}
                onShipmentItemChange={updateShipmentItem}
              />
              {profile?.businessName && profile?.address ? (
                <div style={{ display:'flex', alignItems:'center', gap:12, background:'#F8FAFC', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 12px' }}>
                  {profile.logoData && <img src={profile.logoData} alt="" style={{ width:72, height:46, objectFit:'contain', flexShrink:0 }}/>}
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:900, color:'#1A1A2E' }}>{profile.businessName}</div>
                    <div style={{ fontSize:11, color:'#6B7280', whiteSpace:'pre-line', marginTop:2 }}>{profile.address}</div>
                  </div>
                  <button type="button" onClick={openInvoiceSettings} style={{ marginLeft:'auto', background:'#fff', border:'1px solid #D1D5DB', color:'#374151', borderRadius:8, padding:'7px 9px', fontSize:12, fontWeight:800, cursor:'pointer', flexShrink:0 }}>Edit settings</button>
                </div>
              ) : (
                <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, padding:'10px 12px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ fontSize:12, color:'#92400E', lineHeight:1.4 }}>Add your seller name and address before generating an invoice.</div>
                  <button type="button" onClick={openInvoiceSettings} style={{ background:'#fff', border:'1px solid #F59E0B', color:'#92400E', borderRadius:8, padding:'7px 9px', fontSize:12, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap' }}>Open settings</button>
                </div>
              )}
              {error && <div style={{ fontSize:12, color:'#B91C1C', fontWeight:700 }}>{error}</div>}
              <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                <button onClick={onClose} disabled={generating} style={{ background:'#F3F4F6', border:'1px solid #E5E7EB', color:'#374151', borderRadius:8, padding:'8px 13px', fontSize:13, fontWeight:700, cursor:generating ? 'not-allowed' : 'pointer' }}>Cancel</button>
                <button onClick={generate} disabled={generating || !profile?.businessName || !profile?.address} style={{ background:profile?.businessName && profile?.address ? '#006FDD' : '#E5E7EB', color:profile?.businessName && profile?.address ? '#fff' : '#9CA3AF', border:'none', borderRadius:8, padding:'8px 13px', fontSize:13, fontWeight:800, cursor:generating || !profile?.businessName || !profile?.address ? 'not-allowed' : 'pointer', display:'inline-flex', alignItems:'center', gap:6 }}><FileText size={14}/>{generating ? 'Creating PDF...' : 'Open PDF'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const OrdersScreen = ({ orders, products, ordersError = '', onUpdateOrder, onBulkUpdateOrders, onProductsChanged, compact, showRaw, onRefresh, syncing, dateFormat = 'DD-MM-YYYY', onSetDateFormat, onNavigate }) => {
  const [selected,  setSelected]  = useState(null);
  const [selectedEditing, setSelectedEditing] = useState(false);
  const [defining,  setDefining]  = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [search,    setSearch]    = useState('');
  const [srcF,      setSrcF]      = useState('all');
  const [countryF,  setCountryF]  = useState('all');
  const [stageF,    setStageF]    = useState('all');
  const [hovered,   setHovered]   = useState(null);
  const [bulkWorking, setBulkWorking] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [bulkNotice, setBulkNotice] = useState('');
  const [manualSentOpen, setManualSentOpen] = useState(false);
  const [manualSentNote, setManualSentNote] = useState('');
  const [manualSentSaving, setManualSentSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [aiAuditOpen, setAiAuditOpen] = useState(false);
  const [aiAuditLoading, setAiAuditLoading] = useState(false);
  const [aiAuditResult, setAiAuditResult] = useState(null);
  const [aiAuditError, setAiAuditError] = useState('');
  const [rowMenuOrderId, setRowMenuOrderId] = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [rowActionWorking, setRowActionWorking] = useState('');
  const [rowActionError, setRowActionError] = useState('');
  const [rowActionNotice, setRowActionNotice] = useState('');
  const [fulfillmentSavingIds, setFulfillmentSavingIds] = useState(() => new Set());
  const [visibleFulfillmentWorking, setVisibleFulfillmentWorking] = useState(false);
  const [orderTableSettings, setOrderTableSettings] = useState(DEFAULT_ORDER_TABLE_SETTINGS);
  const [orderTableDraft, setOrderTableDraft] = useState(DEFAULT_ORDER_TABLE_SETTINGS);
  const [orderTableSettingsSaving, setOrderTableSettingsSaving] = useState(false);
  const [customColumnResults, setCustomColumnResults] = useState({});
  const [customVariableEditor, setCustomVariableEditor] = useState(null);
  const customWorkerRef = useRef(null);
  const customSandboxRunRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    api('GET', '/api/order-table-settings').then(({ ok, data }) => {
      if (!ok || cancelled) return;
      const settings = normalizeOrderTableSettings(data?.settings);
      setOrderTableSettings(settings);
      setOrderTableDraft(settings);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    customWorkerRef.current?.terminate();
    customWorkerRef.current = null;
    if (!orderTableSettings.customVariables.length) {
      setCustomColumnResults({});
      return undefined;
    }
    const runId = customSandboxRunRef.current + 1;
    customSandboxRunRef.current = runId;
    const resultsForEveryOrder = result => Object.fromEntries(orders.map(order => [
      order.id,
      Object.fromEntries(orderTableSettings.customVariables.map(variable => [variable.id, result])),
    ]));
    setCustomColumnResults(current => {
      const calculating = {};
      orders.forEach(order => {
        calculating[order.id] = Object.fromEntries(orderTableSettings.customVariables.map(variable => [
          variable.id,
          current[order.id]?.[variable.id] || { value:'Calculating...' },
        ]));
      });
      return calculating;
    });
    let worker;
    try {
      worker = new Worker('/assets/dashboard-compiled/custom-column-worker.js?v=20260807-2');
      customWorkerRef.current = worker;
    } catch (error) {
      setCustomColumnResults(resultsForEveryOrder({ value:'Unavailable', error:String(error?.message || error) }));
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      worker.terminate();
      if (customWorkerRef.current === worker) customWorkerRef.current = null;
      if (customSandboxRunRef.current === runId) {
        setCustomColumnResults(resultsForEveryOrder({ value:'Timed out', error:'Code exceeded the 3 second limit.' }));
      }
    }, 3000);
    worker.onmessage = event => {
      if (event.data?.runId !== runId || customSandboxRunRef.current !== runId) return;
      window.clearTimeout(timeout);
      worker.terminate();
      if (customWorkerRef.current === worker) customWorkerRef.current = null;
      const next = {};
      orders.forEach((order, orderIndex) => {
        next[order.id] = {};
        orderTableSettings.customVariables.forEach((variable, variableIndex) => {
          next[order.id][variable.id] = event.data.results?.[orderIndex]?.[variableIndex]
            || { value:'Unavailable', error:'The custom column did not return a result.' };
        });
      });
      setCustomColumnResults(next);
    };
    worker.onerror = event => {
      window.clearTimeout(timeout);
      worker.terminate();
      if (customWorkerRef.current === worker) customWorkerRef.current = null;
      if (customSandboxRunRef.current === runId) {
        setCustomColumnResults(resultsForEveryOrder({ value:'Unavailable', error:event.message || 'The custom-column worker could not start.' }));
      }
    };
    worker.postMessage({
      runId,
      definitions:orderTableSettings.customVariables.map(({ id, label, code }) => ({ id, label, code })),
      orders,
    });
    return () => {
      window.clearTimeout(timeout);
      worker.terminate();
      if (customWorkerRef.current === worker) customWorkerRef.current = null;
    };
  }, [orders, orderTableSettings]);

  const visibleOrders = orderTableSettings.showIgnoredOrders
    ? orders
    : orders.filter(order => normalizeDashboardOrderStatus(order?.status) !== 'ignored');
  const countryOptions = Array.from(new Set(
    visibleOrders.map(order => String(order.country || '').trim().toUpperCase()).filter(Boolean)
  )).sort((a, b) => {
    const label = code => {
      try { return new Intl.DisplayNames(['en'], { type:'region' }).of(code) || code; }
      catch { return code; }
    };
    return label(a).localeCompare(label(b));
  });
  const countryFilterLabel = code => {
    try { return new Intl.DisplayNames(['en'], { type:'region' }).of(code) || code; }
    catch { return code; }
  };
  const orderMatchesBaseFilters = o => {
    if (srcF !== 'all' && o.source !== srcF) return false;
    if (countryF !== 'all' && String(o.country || '').trim().toUpperCase() !== countryF) return false;
    const name = [o.first_name, o.last_name].join(' ');
    if (search && !`${o.id} ${o.source_id || ''} ${name} ${o.email || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  };
  const pipelineBase = visibleOrders.filter(order => !order.deleted && orderMatchesBaseFilters(order));
  const pipelineCounts = PIPELINE_STAGES.reduce((counts, stage) => ({ ...counts, [stage.value]:0 }), {});
  pipelineBase.forEach(order => {
    PIPELINE_STAGES.forEach(stage => {
      if (orderMatchesPipelineStage(order, stage.value)) pipelineCounts[stage.value] += 1;
    });
  });
  const filtered = visibleOrders.filter(o => {
    if (!orderMatchesBaseFilters(o)) return false;
    if (!orderMatchesPipelineStage(o, stageF)) return false;
    return true;
  });
  const filteredIds = filtered.map(order => order.id);
  const selectedOrders = filtered.filter(order => selectedIds.has(order.id));
  const selectedCount = selectedOrders.length;
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.has(id));
  const someFilteredSelected = filteredIds.some(id => selectedIds.has(id));

  const rowPad = compact ? '7px 14px' : '10px 14px';

  const selectOrderView = view => {
    setStageF(current => current === view ? 'all' : view);
  };

  const toggleSelected = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllFiltered = checked => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      filteredIds.forEach(id => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const selectedDestinationBreakdown = () => {
    const counts = new Map();
    selectedOrders.forEach(order => {
      const routes = fulfillmentRoutesForOrder(order);
      const label = normalizeDashboardOrderStatus(order?.status) === 'ignored'
        ? 'Ignored'
        : routes.length > 1 ? 'Multiple destinations' : routes[0]?.destination || routes[0]?.name || 'No destination';
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 4)
      .map(([label, count]) => `${count} \u2192 ${label}`)
      .join(', ');
  };

  const destinationBreakdown = selectedDestinationBreakdown();
  const bulkActionsDisabled = selectedCount === 0 || !!bulkWorking;
  const bulkButtonStyle = (variant = 'neutral', disabled = bulkActionsDisabled) => ({
    background:disabled ? '#F9FAFB' : '#fff',
    color:disabled ? '#9CA3AF' : variant === 'danger' ? '#991B1B' : '#374151',
    border:`1px solid ${variant === 'danger' && !disabled ? '#FCA5A5' : '#E5E7EB'}`,
    borderRadius:8,
    padding:'7px 10px',
    fontSize:12,
    fontWeight:800,
    cursor:disabled ? 'not-allowed' : 'pointer',
    display:'inline-flex',
    alignItems:'center',
    gap:6,
    whiteSpace:'nowrap',
  });

  const runBulkActionFallback = async action => {
    const status = BULK_STATUS_PATCH[action];
    if (!status) return { ok:false, error:'Invalid bulk action' };
    for (const order of selectedOrders) {
      const result = await api('PATCH', `/api/orders/${encodeURIComponent(order.id)}`, { status });
      if (!result.ok) return { ok:false, error:result.data?.error || 'Could not update selected orders.' };
    }
    return { ok:true };
  };

  const ordersReadyToSend = list => list.filter(order => fulfillmentStatusForOrder(order) === 'routed' && fulfillmentRoutesForOrder(order).length === 1);

  const markOrdersSent = async (ordersToMark, handoffMethod = 'manual', note = '') => {
    const ids = ordersToMark.map(order => order.id);
    if (!ids.length) return { ok:false, error:'Choose orders ready to export with a destination first.' };
    return api('POST', '/api/orders/bulk-action', {
      action:'mark_sent',
      handoffMethod,
      note,
      orderIds:ids,
    });
  };

  const exportAndMarkSent = async ordersToExport => {
    const readyOrders = ordersReadyToSend(ordersToExport);
    setBulkError('');
    if (!readyOrders.length || readyOrders.length !== ordersToExport.length) {
      setBulkError('Export only orders that are ready to export with one destination.');
      return;
    }
    exportOrdersCsv(readyOrders);
    const result = await markOrdersSent(readyOrders, 'csv_download');
    if (!result.ok) {
      setBulkError(result.data?.error || result.error || 'Could not mark orders as sent.');
      return;
    }
    setSelectedIds(new Set());
    onProductsChanged?.();
  };

  const runResendBulk = async () => {
    if (!selectedOrders.length || bulkWorking) return;
    setBulkError('');
    setBulkNotice('');
    setBulkWorking('resend_confirmation_email');
    const { ok, data } = await api('POST', '/api/orders/bulk-action', {
      action:'resend_confirmation_email',
      orderIds:selectedOrders.map(order => order.id),
    });
    setBulkWorking('');
    if (!ok) {
      setBulkError(data?.error || 'Could not queue confirmation emails for the selected orders.');
      return;
    }
    const queued = data?.queued || 0;
    const skippedCount = (data?.skipped || []).length;
    setBulkNotice(
      skippedCount
        ? `Queued ${queued} confirmation email${queued === 1 ? '' : 's'}; skipped ${skippedCount} (${data.skipped[0].reason}${skippedCount > 1 ? ', ...' : ''}).`
        : `Queued ${queued} confirmation email${queued === 1 ? '' : 's'} for resend.`
    );
    setSelectedIds(new Set());
  };

  const runBulkAction = async action => {
    if (!selectedOrders.length || bulkWorking) return;
    setBulkError('');
    setBulkNotice('');
    if (action === 'export') {
      await exportAndMarkSent(selectedOrders);
      return;
    }
    if (action === 'mark_sent') {
      if (ordersReadyToSend(selectedOrders).length !== selectedOrders.length) {
        setBulkError('Mark only orders that are ready to export with one destination as routed.');
        return;
      }
      setManualSentOpen(true);
      return;
    }
    setBulkWorking(action);
    let result;
    try {
      result = await api('POST', '/api/orders/bulk-action', {
        action,
        orderIds:selectedOrders.map(order => order.id),
      });
      if (!result.ok) result = await runBulkActionFallback(action);
    } catch (err) {
      result = await runBulkActionFallback(action);
    }
    setBulkWorking('');
    if (!result.ok) {
      setBulkError(result.error || result.data?.error || 'Could not update selected orders.');
      return;
    }
    setSelectedIds(new Set());
    onProductsChanged?.();
  };

  const confirmManualSent = async () => {
    if (!selectedOrders.length || manualSentSaving) return;
    setManualSentSaving(true);
    setBulkError('');
    const result = await markOrdersSent(selectedOrders, 'manual', manualSentNote);
    setManualSentSaving(false);
    if (!result.ok) {
      setBulkError(result.data?.error || result.error || 'Could not mark selected orders as sent.');
      return;
    }
    setManualSentOpen(false);
    setManualSentNote('');
    setSelectedIds(new Set());
    onProductsChanged?.();
  };

  const refreshVisibleOrders = () => {
    if (onProductsChanged) onProductsChanged();
  };

  const handleUpdate = (id, patch) => {
    if (onUpdateOrder) onUpdateOrder(id, patch);
  };
  const activeSelectedOrder = selected ? orders.find(order => order.id === selected.id) || selected : null;

  const activeOrderCount = orders.filter(order => !order.deleted && normalizeDashboardOrderStatus(order.status) !== 'ignored').length;

  const runAiAudit = async () => {
    if (aiAuditLoading) return;
    setAiAuditOpen(true);
    setAiAuditLoading(true);
    setAiAuditError('');
    const { ok, status, data } = await api('POST', '/api/orders/ai-audit', {});
    setAiAuditLoading(false);
    if (!ok) {
      setAiAuditResult(null);
      setAiAuditError(data?.error || (status === 503 ? 'AI analysis is not configured yet.' : 'AI analysis failed. Please try again in a minute.'));
      return;
    }
    setAiAuditResult(data);
  };

  const openOrderFromAudit = id => {
    const order = orders.find(item => item.id === id);
    if (order) {
      setSelectedEditing(false);
      setSelected(order);
    }
  };

  const setOrderIgnored = async order => {
    if (!order || rowActionWorking) return;
    const isIgnored = normalizeDashboardOrderStatus(order.status) === 'ignored';
    const nextStatus = isIgnored ? 'paid' : 'ignored';
    setRowActionWorking(order.id);
    setRowActionError('');
    setRowActionNotice('');
    const { ok, data } = await api('PATCH', `/api/orders/${encodeURIComponent(order.id)}`, { status:nextStatus });
    setRowActionWorking('');
    setRowMenuOrderId(null);
    if (!ok) {
      setRowActionError(data?.error || `Could not ${isIgnored ? 'restore' : 'ignore'} order ${order.id}.`);
      return;
    }
    handleUpdate(order.id, data?.order || { status:nextStatus });
    onProductsChanged?.();
  };

  const markOrderNeedsReview = async order => {
    if (!order || rowActionWorking) return;
    setRowActionWorking(order.id);
    setRowActionError('');
    setRowActionNotice('');
    const { ok, data } = await api('PATCH', `/api/orders/${encodeURIComponent(order.id)}`, { status:'problem' });
    setRowActionWorking('');
    setRowMenuOrderId(null);
    if (!ok) {
      setRowActionError(data?.error || `Could not mark order ${order.id} as needing review.`);
      return;
    }
    handleUpdate(order.id, data?.order || { status:'problem' });
    onProductsChanged?.();
  };

  const resendConfirmationEmail = async order => {
    if (!order || rowActionWorking) return;
    setRowActionWorking(order.id);
    setRowActionError('');
    setRowActionNotice('');
    setRowMenuOrderId(null);
    const { ok, data } = await api('POST', `/api/orders/${encodeURIComponent(order.id)}/resend-confirmation-email`, {});
    setRowActionWorking('');
    if (!ok) {
      setRowActionError(data?.error || `Could not resend the confirmation email for order ${order.id}.`);
      return;
    }
    const failed = (data?.results || []).find(r => !r.ok);
    if (failed) {
      setRowActionError(failed.error || `Brevo could not send the confirmation email for order ${order.id}.`);
      return;
    }
    setRowActionNotice(`Confirmation email resent for order ${order.id}.`);
  };

  const toggleOrderFulfilled = async order => {
    if (!order || fulfillmentSavingIds.has(order.id)) return;
    const fulfilled = !Boolean(order.fulfilled);
    setFulfillmentSavingIds(current => new Set(current).add(order.id));
    setRowActionError('');
    const { ok, data } = await api('PATCH', `/api/orders/${encodeURIComponent(order.id)}`, { fulfilled });
    setFulfillmentSavingIds(current => {
      const next = new Set(current);
      next.delete(order.id);
      return next;
    });
    if (!ok) {
      setRowActionError(data?.error || `Could not update fulfillment for order ${order.id}.`);
      return;
    }
    handleUpdate(order.id, data?.order || { fulfilled:fulfilled ? 1 : 0 });
  };

  const setVisibleFulfilled = async fulfilled => {
    if (!filteredIds.length || visibleFulfillmentWorking) return;
    setVisibleFulfillmentWorking(fulfilled ? 'fulfilled' : 'unfulfilled');
    setSettingsMessage('');
    setSettingsError('');
    for (let index = 0; index < filteredIds.length; index += 500) {
      const { ok, data } = await api('POST', '/api/orders/bulk-action', {
        action:fulfilled ? 'mark_fulfilled' : 'clear_fulfilled',
        orderIds:filteredIds.slice(index, index + 500),
      });
      if (!ok) {
        setVisibleFulfillmentWorking(false);
        setSettingsError(data?.error || 'Could not update all visible orders.');
        onProductsChanged?.();
        return;
      }
    }
    if (onBulkUpdateOrders) onBulkUpdateOrders(filteredIds, { fulfilled:fulfilled ? 1 : 0 });
    else onProductsChanged?.();
    setVisibleFulfillmentWorking(false);
    setSettingsMessage(`${filteredIds.length} visible order${filteredIds.length === 1 ? '' : 's'} marked ${fulfilled ? 'fulfilled' : 'unfulfilled'}.`);
  };

  const customVariableByColumnId = Object.fromEntries(
    orderTableSettings.customVariables.map(variable => [`custom:${variable.id}`, variable])
  );
  const visibleMainColumns = orderTableSettings.columnOrder
    .filter(id => !orderTableSettings.hiddenColumns.includes(id))
    .map(id => ORDER_TABLE_CORE_COLUMNS.find(column => column.id === id) || (customVariableByColumnId[id] ? { id, label:customVariableByColumnId[id].label, width:160, custom:true } : null))
    .filter(Boolean);
  const orderTableMinWidth = 42 + 58 + 88 + 80 + 50 + visibleMainColumns.reduce((sum, column) => sum + column.width, 0);

  const openOrderSettings = () => {
    setOrderTableDraft(normalizeOrderTableSettings(orderTableSettings));
    setCustomVariableEditor(null);
    setSettingsOpen(true);
    setSettingsMessage('');
    setSettingsError('');
  };

  const moveDraftColumn = (id, direction) => {
    setOrderTableDraft(current => {
      const index = current.columnOrder.indexOf(id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.columnOrder.length) return current;
      const columnOrder = [...current.columnOrder];
      [columnOrder[index], columnOrder[target]] = [columnOrder[target], columnOrder[index]];
      return { ...current, columnOrder };
    });
  };

  const toggleDraftColumn = id => {
    setOrderTableDraft(current => ({
      ...current,
      hiddenColumns:current.hiddenColumns.includes(id)
        ? current.hiddenColumns.filter(value => value !== id)
        : [...current.hiddenColumns, id],
    }));
  };

  const saveCustomVariable = () => {
    const label = String(customVariableEditor?.label || '').trim();
    const code = String(customVariableEditor?.code || '').trim();
    if (!label || !code) {
      setSettingsError('Add a column name and JavaScript code.');
      return;
    }
    const id = customVariableEditor.id || `variable_${Date.now().toString(36)}`;
    setOrderTableDraft(current => {
      const exists = current.customVariables.some(variable => variable.id === id);
      return normalizeOrderTableSettings({
        ...current,
        customVariables:exists
          ? current.customVariables.map(variable => variable.id === id ? { id, label, code } : variable)
          : [...current.customVariables, { id, label, code }],
        columnOrder:exists ? current.columnOrder : [...current.columnOrder, `custom:${id}`],
      });
    });
    setCustomVariableEditor(null);
    setSettingsError('');
  };

  const removeCustomVariable = id => {
    const columnId = `custom:${id}`;
    setOrderTableDraft(current => normalizeOrderTableSettings({
      ...current,
      customVariables:current.customVariables.filter(variable => variable.id !== id),
      columnOrder:current.columnOrder.filter(value => value !== columnId),
      hiddenColumns:current.hiddenColumns.filter(value => value !== columnId),
    }));
    if (customVariableEditor?.id === id) setCustomVariableEditor(null);
  };

  const saveOrderTableSettings = async () => {
    if (orderTableSettingsSaving) return;
    setOrderTableSettingsSaving(true);
    setSettingsError('');
    const { ok, data } = await api('PATCH', '/api/order-table-settings', orderTableDraft);
    setOrderTableSettingsSaving(false);
    if (!ok) {
      setSettingsError(data?.error || 'Could not save table columns.');
      return;
    }
    const settings = normalizeOrderTableSettings(data?.settings);
    setOrderTableSettings(settings);
    setOrderTableDraft(settings);
    setSettingsMessage('Table columns saved.');
  };

  const renderMainOrderCell = (column, order, reviewIssues) => {
    if (column.custom) {
      const result = customColumnResults[order.id]?.[column.id.slice(7)] || { value:'' };
      return <td key={column.id} title={result.error || result.value || ''} style={{ padding:rowPad, color:result.error ? '#B91C1C' : '#374151', fontSize:12, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{result.value || '-'}</td>;
    }
    if (column.id === 'order_id') return <td key={column.id} style={{ padding:'7px 8px', fontFamily:'monospace', fontSize:11, color:'#374151', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}><span>{order.id}</span>{isEditedOrder(order) && <EditedOrderMarker style={{ marginLeft:4 }} />}<ReviewIssueMarker issues={reviewIssues} style={{ marginLeft:4 }} /></td>;
    if (column.id === 'date') return <td key={column.id} style={{ padding:'7px 8px', color:'#9CA3AF', whiteSpace:'nowrap', fontSize:11 }}>{formatDate(order.date, dateFormat)}</td>;
    if (column.id === 'name') return <td key={column.id} style={{ padding:rowPad, fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{[order.first_name, order.last_name].filter(Boolean).join(' ') || '-'}</td>;
    if (column.id === 'email') return <td key={column.id} style={{ padding:rowPad, color:'#6B7280', fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{order.email || '-'}</td>;
    if (column.id === 'phone') {
      const { code, rest } = splitPhoneByCountry(order.phone, order.country);
      return <td key={column.id} style={{ padding:rowPad, whiteSpace:'nowrap', fontSize:12, color:'#374151', overflow:'hidden', textOverflow:'ellipsis' }}>{!rest ? <span style={{ color:'#9CA3AF' }}>-</span> : <span title={`${code} ${rest}`.trim()} style={{ fontFamily:'monospace', fontSize:12 }}>{code && <span style={{ color:'#9CA3AF', fontWeight:600 }}>{code}&nbsp;</span>}<span style={{ color:'#374151' }}>{rest}</span></span>}</td>;
    }
    if (column.id === 'location') {
      const ctry = cleanLocationPart(order.country);
      const parts = orderLocationParts(order);
      return <td key={column.id} style={{ padding:rowPad, whiteSpace:'nowrap', fontSize:12, color:'#374151', overflow:'hidden', textOverflow:'ellipsis' }}>{!parts.length ? <span style={{ color:'#9CA3AF' }}>N/A</span> : <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><span>{parts.join(', ')}</span>{ctry?.length === 2 && <img src={`/assets/circle-flags/flags/${ctry.toLowerCase()}.svg`} alt={ctry} style={{ width:16, height:16, flexShrink:0 }}/>}</span>}</td>;
    }
    if (column.id === 'product') return <td key={column.id} style={{ padding:rowPad, overflow:'hidden' }}><ProductCell order={order} onDefine={setDefining}/></td>;
    if (column.id === 'amount') return <td key={column.id} style={{ padding:'7px 8px', fontFamily:'monospace', fontSize:12, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{order.amount ? `${order.amount} ${(order.currency||'').toUpperCase()}` : '-'}</td>;
    return null;
  };

  return (
    <div className="dash-page" style={{ padding:0, background:'#fff' }}>
      <div className="dash-panel" style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', border:'none', borderRadius:0, boxShadow:'none' }}>
        <div className="dash-panel-header">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap', paddingBottom:10, borderBottom:'1px solid #F3F4F6' }}>
	          <h1 className="dash-page-title">Orders</h1>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search orders…" style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, outline:'none', width:260, maxWidth:'min(260px, 100%)', background:'#fff' }}/>
            <button onClick={() => setManualOpen(true)} style={{ background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap' }}>
	              <Plus size={13} strokeWidth={2.4}/>
	              Add order
	            </button>
	          </div>
	        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap', paddingTop:10 }}>
          <div style={{ fontSize:13, color:'#6B7280' }}>
            {visibleOrders.length > 0 ? (
              <>Showing <span style={{ fontWeight:700, color:'#1A1A2E' }}>{filtered.length}</span> of <span style={{ fontWeight:700, color:'#1A1A2E' }}>{visibleOrders.length}</span> order{visibleOrders.length !== 1 ? 's' : ''}</>
            ) : ordersError ? (
              'Orders could not load'
            ) : (
              'No orders yet'
            )}
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
	            <select value={stageF} onChange={e => setStageF(e.target.value)} aria-label="Filter orders by status" style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:'#F9FAFB', cursor:'pointer' }}>
	              <option value="all">All statuses</option>
	              {FLAG_STAGES.map(stage => <option key={stage.value} value={stage.value}>{stage.label} ({pipelineCounts[stage.value] || 0})</option>)}
	            </select>
	            <select value={srcF} onChange={e => setSrcF(e.target.value)} style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:'#F9FAFB', cursor:'pointer' }}>
	              <option value="all">All Sources</option>
	              <option value="stripe">Stripe</option>
              <option value="paypal">PayPal</option>
              <option value="shopify">Shopify</option>
              <option value="woocommerce">WooCommerce</option>
              <option value="adobe_commerce">Adobe Commerce</option>
	              <option value="kickstarter">Kickstarter</option>
	              <option value="manual">Manual</option>
	            </select>
	            <select value={countryF} onChange={e => setCountryF(e.target.value)} aria-label="Filter orders by country" style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:'#F9FAFB', cursor:'pointer', maxWidth:190 }}>
	              <option value="all">All countries</option>
	              {countryOptions.map(code => <option key={code} value={code}>{countryFilterLabel(code)} ({code})</option>)}
	            </select>
	            <button onClick={onRefresh} disabled={syncing} style={{ background:'#F9FAFB', color: syncing ? '#9CA3AF' : '#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'7px 11px', fontSize:13, fontWeight:600, cursor: syncing ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>
	              <RefreshCw size={13} strokeWidth={2.2} style={{ animation: syncing ? 'spin 0.8s linear infinite' : 'none' }}/>
	              {syncing ? 'Syncing…' : 'Sync'}
            </button>
            <div>
              <button onClick={openOrderSettings} title="Order settings" style={{ background:'#F9FAFB', color:'#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'7px 10px', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center' }}>
                <Settings size={14} strokeWidth={2}/>
              </button>
	            </div>
	          </div>
	        </div>
	        {(selectedCount > 0 || bulkError) && <div style={{ marginTop:12, border:'1px solid #E5E7EB', background:'#F8FAFC', borderRadius:8, padding:'8px 10px', minHeight:42, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
	          <div style={{ minWidth:0, display:'flex', alignItems:'center', gap:8, color:'#374151', fontSize:12, fontWeight:800, overflow:'hidden' }}>
	            {selectedCount > 0 ? (
	              <>
	                <CircleSelectControl
	                  checked={allFilteredSelected}
	                  mixed={!allFilteredSelected && someFilteredSelected}
	                  disabled={!filteredIds.length}
	                  label="Select all visible orders"
	                  onChange={toggleAllFiltered}
	                />
	                <span style={{ whiteSpace:'nowrap' }}>{selectedCount} selected</span>
	                {destinationBreakdown && (
	                  <span style={{ color:'#6B7280', fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{destinationBreakdown}</span>
	                )}
	              </>
	            ) : null}
	          </div>
	          <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap', justifyContent:'flex-end' }}>
	            {bulkError && <span style={{ color:'#B91C1C', fontSize:12, fontWeight:800 }}>{bulkError}</span>}
	            {!bulkError && bulkNotice && <span style={{ color:'#166534', fontSize:12, fontWeight:800 }}>{bulkNotice}</span>}
	            {selectedCount > 0 ? (
	              <>
	                <button onClick={() => runBulkAction('export')} disabled={bulkActionsDisabled} style={bulkButtonStyle('primary')}>
	                  <Download size={13}/>{'Export selected'}
	                </button>
	                <button onClick={() => runBulkAction('reroute')} disabled={bulkActionsDisabled} style={bulkButtonStyle()}>
	                  {bulkWorking === 'reroute' ? 'Re-routing...' : 'Re-route'}
	                </button>
	                <button onClick={() => runBulkAction('mark_sent')} disabled={bulkActionsDisabled} style={bulkButtonStyle()}>
	                  Mark routed manually
	                </button>
	                <button onClick={() => runBulkAction('mark_needs_review')} disabled={bulkActionsDisabled} style={bulkButtonStyle()}>
	                  {bulkWorking === 'mark_needs_review' ? 'Saving...' : 'Mark needs review'}
	                </button>
	                <button onClick={runResendBulk} disabled={bulkActionsDisabled} style={bulkButtonStyle()}>
	                  <Send size={13}/>{bulkWorking === 'resend_confirmation_email' ? 'Queuing...' : 'Resend confirmation emails'}
	                </button>
	                <button onClick={() => runBulkAction('ignore')} disabled={bulkActionsDisabled} style={bulkButtonStyle()}>
	                  {bulkWorking === 'ignore' ? 'Ignoring...' : 'Ignore'}
	                </button>
	              </>
	            ) : null}
	          </div>
	        </div>}
        </div>

      <div className="dash-attached orders-table-scroll" style={{ overflow:'auto', flex:1, minHeight:0 }}>
        {ordersError && visibleOrders.length === 0 ? (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', padding:48, color:'#9CA3AF', gap:12 }}>
            <AlertTriangle size={40} strokeWidth={1.4} color="#DC2626"/>
            <div style={{ fontSize:14, fontWeight:700, color:'#B91C1C' }}>Orders could not load</div>
            <div style={{ fontSize:13, textAlign:'center' }}>Your orders were not cleared. The dashboard could not read the orders API response.</div>
            <button onClick={onRefresh} style={{ border:'1px solid #D1D5DB', background:'#fff', borderRadius:8, padding:'8px 12px', fontSize:13, fontWeight:800, color:'#374151', cursor:'pointer', whiteSpace:'nowrap' }}>Retry</button>
          </div>
        ) : visibleOrders.length === 0 ? (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', padding:48, color:'#9CA3AF', gap:12 }}>
            <Inbox size={40} strokeWidth={1.2} color="#D1D5DB"/>
            <div style={{ fontSize:14, fontWeight:600, color:'#6B7280' }}>No orders yet</div>
            <div style={{ fontSize:13, textAlign:'center' }}>Connect a source and run an import to see orders here.</div>
          </div>
        ) : (
	          <>
	            <table className="orders-table workspace-table" style={{ width:'100%', minWidth:Math.max(520, orderTableMinWidth), tableLayout:'fixed', borderCollapse:'separate', borderSpacing:0, fontSize:13 }}>
	              <colgroup>
	                <col style={{ width:42 }}/>
	                <col style={{ width:58 }}/>
	                {visibleMainColumns.map(column => <col key={column.id} style={{ width:column.width }}/>) }
	                <col style={{ width:88 }}/>
	                <col style={{ width:80 }}/>
	                <col style={{ width:50 }}/>
	              </colgroup>
		              <thead style={{ position:'sticky', top:0, zIndex:5 }}>
		                <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
		                  <th style={{ position:'sticky', top:0, zIndex:6, background:'#F9FAFB', padding:'8px 8px', textAlign:'center', boxShadow:'inset 0 -1px 0 #E5E7EB' }}>
		                    <CircleSelectControl
		                      checked={allFilteredSelected}
		                      mixed={!allFilteredSelected && someFilteredSelected}
		                      disabled={!filteredIds.length}
		                      label="Select all visible orders"
		                      onChange={toggleAllFiltered}
		                    />
		                  </th>
	                  {[{ id:'source', label:'Source' }, ...visibleMainColumns, { id:'flags', label:'Flags' }, { id:'fulfilled', label:'Fulfilled' }, { id:'actions', label:'Actions' }].map(col => (
	                    <th
	                      key={col.id}
	                      title={col.label}
	                      className={col.id === 'source' ? 'orders-source-col orders-source-col-header' : undefined}
	                      style={{ position:'sticky', top:0, zIndex:6, background:'#F9FAFB', padding:'9px 10px', textAlign: ['source', 'fulfilled'].includes(col.id) ? 'center' : 'left', fontSize:11, fontWeight:600, color:'#9CA3AF', letterSpacing:'0.05em', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', boxShadow:'inset 0 -1px 0 #E5E7EB' }}
	                    >
	                      {['flags', 'actions'].includes(col.id) ? '' : col.label}
	                    </th>
	                  ))}
	                </tr>
	              </thead>
	              <tbody>
	                {filtered.map((order,i) => {
	                  const reviewIssues = orderReviewIssues(order);
	                  const isSelected = selectedIds.has(order.id);
	                  return (
		                  <tr key={order.id}
		                    onClick={() => { setRowMenuOrderId(null); setSelectedEditing(false); setSelected(order); }}
		                    onMouseEnter={() => setHovered(order.id)}
		                    onMouseLeave={() => setHovered(null)}
		                    style={{ borderBottom:'1px solid #F3F4F6', cursor:'pointer', background: isSelected ? '#F1F5F9' : hovered===order.id ? '#F8FAFC' : i%2===0 ? '#fff' : '#FAFAFA', transition:'background 0.08s' }}>
		                    <td onClick={e => e.stopPropagation()} style={{ padding:'7px 8px', textAlign:'center' }}>
		                      <CircleSelectControl
		                        checked={isSelected}
		                        label={`Select order ${order.id}`}
		                        onChange={checked => toggleSelected(order.id, checked)}
		                      />
		                    </td>
	                    <td className="orders-source-col orders-source-col-cell" style={{ padding:'7px 8px', textAlign:'center' }}>
	                      <div title={PLATFORM_CFG[order.source]?.label || order.source || 'Source'} style={{ display:'inline-flex' }}><PlatformPill source={order.source}/></div>
	                    </td>
	                    {visibleMainColumns.map(column => renderMainOrderCell(column, order, reviewIssues))}
	                    <td style={{ padding:'7px 8px' }}>
	                      <OrderFlagTag order={order} issues={reviewIssues}/>
	                    </td>
	                    <td onClick={e => e.stopPropagation()} style={{ padding:'5px 7px', textAlign:'center' }}>
	                      <FulfilledToggle
	                        checked={Boolean(order.fulfilled)}
	                        saving={fulfillmentSavingIds.has(order.id)}
	                        orderId={order.id}
	                        onToggle={() => toggleOrderFulfilled(order)}
	                      />
	                    </td>
	                    <td onClick={e => e.stopPropagation()} style={{ padding:'5px 7px', position:'relative', textAlign:'center' }}>
	                      <button
	                        type="button"
	                        aria-label={`More actions for order ${order.id}`}
	                        aria-expanded={rowMenuOrderId === order.id}
	                        onClick={() => { setRowMenuOrderId(current => current === order.id ? null : order.id); setRowActionError(''); }}
	                        style={{ width:30, height:30, border:'1px solid transparent', borderRadius:7, background:rowMenuOrderId === order.id ? '#F1F5F9' : 'transparent', color:'#6B7280', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}
	                      >
	                        <EllipsisVertical size={16} strokeWidth={2.1}/>
	                      </button>
	                      {rowMenuOrderId === order.id && (
	                        <div style={{ position:'absolute', zIndex:20, right:7, ...(i >= filtered.length - 3 ? { bottom:37 } : { top:37 }), width:190, background:'#fff', border:'1px solid #E5E7EB', borderRadius:9, boxShadow:'0 12px 32px rgba(15,23,42,0.16)', padding:5, textAlign:'left' }}>
	                          <button
	                            onClick={() => { setSelectedEditing(true); setSelected(order); setRowMenuOrderId(null); }}
	                            style={{ width:'100%', border:'none', borderRadius:7, background:'transparent', padding:'8px 9px', color:'#374151', fontSize:12, fontWeight:700, cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:7 }}
	                          >
	                            <Edit3 size={13} strokeWidth={2.2}/>Edit order
	                          </button>
	                          <button
	                            onClick={() => setOrderIgnored(order)}
	                            disabled={!!rowActionWorking}
	                            style={{ width:'100%', border:'none', borderRadius:7, background:'transparent', padding:'8px 9px', color:'#374151', fontSize:12, fontWeight:700, cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:7 }}
	                          >
	                            {normalizeDashboardOrderStatus(order.status) === 'ignored' ? <Eye size={13} strokeWidth={2.2}/> : <EyeOff size={13} strokeWidth={2.2}/>}
	                            {rowActionWorking === order.id ? 'Saving...' : normalizeDashboardOrderStatus(order.status) === 'ignored' ? 'Restore order' : 'Ignore order'}
	                          </button>
	                          <button
	                            onClick={() => markOrderNeedsReview(order)}
	                            disabled={normalizeDashboardOrderStatus(order.status) === 'problem' || !!rowActionWorking}
	                            style={{ width:'100%', border:'none', borderRadius:7, background:'transparent', padding:'8px 9px', color:normalizeDashboardOrderStatus(order.status) === 'problem' ? '#9CA3AF' : '#374151', fontSize:12, fontWeight:700, cursor:normalizeDashboardOrderStatus(order.status) === 'problem' ? 'not-allowed' : 'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:7 }}
	                          >
	                            <AlertTriangle size={13}/>{normalizeDashboardOrderStatus(order.status) === 'problem' ? 'Needs review' : 'Mark needs review'}
	                          </button>
	                          <button
	                            onClick={() => { setInvoiceOrder(order); setRowMenuOrderId(null); }}
	                            style={{ width:'100%', border:'none', borderRadius:7, background:'transparent', padding:'8px 9px', color:'#374151', fontSize:12, fontWeight:700, cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:7 }}
	                          >
	                            <FileText size={13}/>Generate invoice
	                          </button>
	                          <button
	                            onClick={() => resendConfirmationEmail(order)}
	                            disabled={!!rowActionWorking}
	                            style={{ width:'100%', border:'none', borderRadius:7, background:'transparent', padding:'8px 9px', color:'#374151', fontSize:12, fontWeight:700, cursor:rowActionWorking ? 'wait' : 'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:7 }}
	                          >
	                            <Send size={13}/>{rowActionWorking === order.id ? 'Sending...' : 'Resend confirmation email'}
	                          </button>
	                        </div>
	                      )}
	                    </td>
	                  </tr>
	                  );
	                })}
	              </tbody>
	            </table>
		            <div style={{ padding:'8px 14px', borderTop:'1px solid #F3F4F6', fontSize:12, color:'#9CA3AF' }}>
	              Click a row to view details
	            </div>
	          </>
        )}
      </div>
      </div>

      <OrderAiAuditModal
        open={aiAuditOpen}
        loading={aiAuditLoading}
        result={aiAuditResult}
        error={aiAuditError}
        orderCount={Math.min(activeOrderCount, 300)}
        onClose={() => setAiAuditOpen(false)}
        onOpenOrder={openOrderFromAudit}
      />
      {rowActionError && (
        <div role="alert" style={{ position:'fixed', right:20, bottom:20, zIndex:10030, maxWidth:360, background:'#FEF2F2', color:'#B91C1C', border:'1px solid #FECACA', borderRadius:9, padding:'10px 12px', fontSize:12, fontWeight:700, boxShadow:'0 10px 28px rgba(15,23,42,0.14)' }}>
          {rowActionError}
        </div>
      )}
      {!rowActionError && rowActionNotice && (
        <div role="status" style={{ position:'fixed', right:20, bottom:20, zIndex:10030, maxWidth:360, background:'#F0FDF4', color:'#166534', border:'1px solid #BBF7D0', borderRadius:9, padding:'10px 12px', fontSize:12, fontWeight:700, boxShadow:'0 10px 28px rgba(15,23,42,0.14)' }}>
          {rowActionNotice}
        </div>
      )}
      {invoiceOrder && <InvoiceGeneratorModal order={invoiceOrder} onClose={() => setInvoiceOrder(null)} onNavigate={onNavigate}/>}
      {manualOpen && (
        <ManualOrderModal
          products={products || []}
          onClose={() => setManualOpen(false)}
          onAdded={() => { setManualOpen(false); onProductsChanged?.(); }}
          onProductsChanged={onProductsChanged}
        />
      )}
      {activeSelectedOrder && <OrderDrawer key={`${activeSelectedOrder.id}-${selectedEditing ? 'edit' : 'view'}`} order={activeSelectedOrder} initialEditing={selectedEditing} onClose={() => { setSelected(null); setSelectedEditing(false); }} showRaw={showRaw} onUpdate={handleUpdate} onChanged={refreshVisibleOrders} dateFormat={dateFormat} onDefine={setDefining}/>}
	      {defining && (
	        <ProductAssignmentModal
	          order={defining.order || defining}
	          item={defining.item}
	          products={products || []}
	          onClose={() => setDefining(null)}
          onAssigned={() => { setDefining(null); onProductsChanged?.(); }}
        />
      )}
      {manualSentOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.48)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000, padding:18 }}
             onClick={() => !manualSentSaving && setManualSentOpen(false)}>
          <div style={{ background:'#fff', borderRadius:14, padding:22, width:420, maxWidth:'100%', boxShadow:'0 20px 60px rgba(15,23,42,0.24)' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:16, fontWeight:800, color:'#1A1A2E', marginBottom:8 }}>Mark selected orders as routed?</div>
            <div style={{ fontSize:13, color:'#6B7280', marginBottom:14, lineHeight:1.5 }}>
              This records that fulfillment was handled outside Omnifill. Use it only when the destination already has the order.
            </div>
            <label style={{ display:'block', fontSize:11, fontWeight:800, color:'#9CA3AF', letterSpacing:'0.06em', marginBottom:6 }}>NOTE OPTIONAL</label>
            <textarea
              value={manualSentNote}
              onChange={e => setManualSentNote(e.target.value)}
              placeholder="Add handoff context..."
              rows={3}
              style={{ width:'100%', resize:'vertical', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', color:'#1A1A2E', marginBottom:16 }}
            />
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <div style={{ fontSize:12, color:'#9CA3AF', fontWeight:700 }}>{selectedCount} selected</div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:10, flexWrap:'wrap' }}>
                <button onClick={() => setManualSentOpen(false)} disabled={manualSentSaving} style={{ background:'#F3F4F6', border:'1px solid #E5E7EB', color:'#374151', borderRadius:8, padding:'8px 13px', fontSize:13, fontWeight:700, cursor:manualSentSaving ? 'not-allowed' : 'pointer' }}>
                  Cancel
                </button>
                <button onClick={confirmManualSent} disabled={manualSentSaving} style={{ background:'#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'8px 13px', fontSize:13, fontWeight:800, cursor:manualSentSaving ? 'not-allowed' : 'pointer', opacity:manualSentSaving ? 0.65 : 1 }}>
                  {manualSentSaving ? 'Saving...' : 'Mark routed'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {settingsOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.42)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9998, padding:18 }}
             onClick={() => !visibleFulfillmentWorking && setSettingsOpen(false)}>
          <div style={{ background:'#fff', width:560, maxWidth:'100%', maxHeight:'calc(100vh - 36px)', overflow:'auto', borderRadius:14, border:'1px solid #E5E7EB', boxShadow:'0 24px 70px rgba(15,23,42,0.24)' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, padding:'18px 20px', borderBottom:'1px solid #F3F4F6' }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:'#1A1A2E' }}>Order settings</div>
                <div style={{ fontSize:12, color:'#9CA3AF', marginTop:3 }}>Table display and order actions.</div>
              </div>
              <button onClick={() => setSettingsOpen(false)} disabled={!!visibleFulfillmentWorking} title="Close" style={{ width:32, height:32, border:'1px solid #E5E7EB', borderRadius:8, background:'#F9FAFB', color:'#374151', cursor:visibleFulfillmentWorking ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <X size={15} strokeWidth={2.2}/>
              </button>
            </div>
            <div style={{ padding:'18px 20px', display:'grid', gap:14 }}>
              <div style={{ border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
                <div style={{ padding:'14px 16px', borderBottom:'1px solid #F3F4F6' }}>
                  <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E' }}>Display</div>
                  <div style={{ fontSize:12, color:'#9CA3AF', marginTop:3 }}>Choose how order dates appear in the table.</div>
                </div>
                <div style={{ padding:'14px 16px', display:'grid', gridTemplateColumns:'160px minmax(0, 1fr)', gap:14, alignItems:'center' }}>
                  <span style={{ fontSize:13, color:'#6B7280' }}>Date format</span>
                  <select value={dateFormat} onChange={e => onSetDateFormat?.(e.target.value)} style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', background:'#fff', color:'#1A1A2E', cursor:'pointer', maxWidth:240 }}>
                    {DATE_FORMATS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
                <div style={{ padding:'14px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E' }}>Table columns</div>
                    <div style={{ fontSize:12, color:'#6B7280', marginTop:3 }}>Reorder or hide the main columns, and add computed data.</div>
                  </div>
                  <button type="button" onClick={() => setCustomVariableEditor({ label:'', code:'return order.country === "US" ? "Domestic" : "International";' })} disabled={orderTableDraft.customVariables.length >= 12} style={{ background:'#fff', color:orderTableDraft.customVariables.length >= 12 ? '#9CA3AF' : '#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'7px 9px', fontSize:12, fontWeight:800, cursor:orderTableDraft.customVariables.length >= 12 ? 'not-allowed' : 'pointer', whiteSpace:'nowrap' }}>
                    Add custom
                  </button>
                </div>
                <div style={{ padding:'8px 12px' }}>
                  {orderTableDraft.columnOrder.map((id, index) => {
                    const core = ORDER_TABLE_CORE_COLUMNS.find(column => column.id === id);
                    const custom = id.startsWith('custom:') ? orderTableDraft.customVariables.find(variable => variable.id === id.slice(7)) : null;
                    if (!core && !custom) return null;
                    const visible = !orderTableDraft.hiddenColumns.includes(id);
                    return (
                      <div key={id} style={{ minHeight:42, display:'grid', gridTemplateColumns:'26px minmax(0, 1fr) auto', gap:8, alignItems:'center', borderTop:index ? '1px solid #F3F4F6' : 'none' }}>
                        <GripVertical size={15} color="#9CA3AF"/>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:800, color:visible ? '#1A1A2E' : '#9CA3AF', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{core?.label || custom?.label}</div>
                          {custom && <div style={{ fontSize:10, color:'#9CA3AF', marginTop:1 }}>Custom JavaScript</div>}
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                          {custom && <button type="button" onClick={() => setCustomVariableEditor({ ...custom })} title="Edit custom column" style={{ width:28, height:28, display:'inline-flex', alignItems:'center', justifyContent:'center', border:'1px solid #E5E7EB', borderRadius:7, background:'#fff', color:'#374151', cursor:'pointer' }}><Edit3 size={12}/></button>}
                          <button type="button" onClick={() => moveDraftColumn(id, -1)} disabled={index === 0} title="Move up" style={{ width:28, height:28, display:'inline-flex', alignItems:'center', justifyContent:'center', border:'1px solid #E5E7EB', borderRadius:7, background:index === 0 ? '#F3F4F6' : '#fff', color:index === 0 ? '#9CA3AF' : '#374151', cursor:index === 0 ? 'not-allowed' : 'pointer' }}><ChevronUp size={12}/></button>
                          <button type="button" onClick={() => moveDraftColumn(id, 1)} disabled={index === orderTableDraft.columnOrder.length - 1} title="Move down" style={{ width:28, height:28, display:'inline-flex', alignItems:'center', justifyContent:'center', border:'1px solid #E5E7EB', borderRadius:7, background:index === orderTableDraft.columnOrder.length - 1 ? '#F3F4F6' : '#fff', color:index === orderTableDraft.columnOrder.length - 1 ? '#9CA3AF' : '#374151', cursor:index === orderTableDraft.columnOrder.length - 1 ? 'not-allowed' : 'pointer' }}><ChevronDown size={12}/></button>
                          <button type="button" onClick={() => toggleDraftColumn(id)} title={visible ? 'Hide column' : 'Show column'} style={{ width:28, height:28, display:'inline-flex', alignItems:'center', justifyContent:'center', border:'1px solid #E5E7EB', borderRadius:7, background:'#fff', color:visible ? '#374151' : '#9CA3AF', cursor:'pointer' }}>{visible ? <Eye size={12}/> : <EyeOff size={12}/>}</button>
                          {custom && <button type="button" onClick={() => removeCustomVariable(custom.id)} title="Delete custom column" style={{ width:28, height:28, display:'inline-flex', alignItems:'center', justifyContent:'center', border:'1px solid #FECACA', borderRadius:7, background:'#fff', color:'#DC2626', cursor:'pointer' }}><Trash2 size={12}/></button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {customVariableEditor && (
                  <div style={{ padding:'14px 16px', borderTop:'1px solid #E5E7EB', background:'#F8FAFC', display:'grid', gap:9 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
                      <div style={{ fontSize:12, fontWeight:800, color:'#1A1A2E' }}>{customVariableEditor.id ? 'Edit custom column' : 'New custom column'}</div>
                      <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('omnifill:open-assistant'))} style={{ background:'transparent', border:'none', color:'#006FDD', padding:0, fontSize:11, fontWeight:800, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5 }}><Sparkles size={12}/>Ask AI for help</button>
                    </div>
                    <input value={customVariableEditor.label} onChange={event => setCustomVariableEditor(current => ({ ...current, label:event.target.value }))} placeholder="Column name" maxLength={80} style={{ border:'1px solid #D1D5DB', borderRadius:8, padding:'8px 10px', fontSize:12, outline:'none', background:'#fff' }}/>
                    <textarea value={customVariableEditor.code} onChange={event => setCustomVariableEditor(current => ({ ...current, code:event.target.value }))} spellCheck={false} rows={7} aria-label="Custom column JavaScript" style={{ width:'100%', resize:'vertical', border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 10px', fontFamily:'monospace', fontSize:11, lineHeight:1.5, outline:'none', background:'#111827', color:'#F9FAFB' }}/>
                    <div style={{ fontSize:11, color:'#6B7280', lineHeight:1.45 }}>Return a value using <code>order</code>. Earlier custom values are available by name through <code>columns</code>. Helpers include <code>coalesce</code>, <code>upper</code>, <code>lower</code>, <code>money</code>, and <code>number</code>. Code runs read-only in an isolated sandbox with a 3 second limit.</div>
                    <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                      <button type="button" onClick={() => setCustomVariableEditor(null)} style={{ background:'#fff', border:'1px solid #D1D5DB', color:'#374151', borderRadius:8, padding:'7px 10px', fontSize:12, fontWeight:800, cursor:'pointer' }}>Cancel</button>
                      <button type="button" onClick={saveCustomVariable} style={{ background:'#006FDD', border:'none', color:'#fff', borderRadius:8, padding:'7px 11px', fontSize:12, fontWeight:800, cursor:'pointer' }}>Keep column</button>
                    </div>
                  </div>
                )}
                <div style={{ padding:'12px 16px', borderTop:'1px solid #F3F4F6', display:'flex', justifyContent:'flex-end' }}>
                  <button type="button" onClick={saveOrderTableSettings} disabled={orderTableSettingsSaving || !!customVariableEditor} style={{ background:customVariableEditor ? '#E5E7EB' : '#006FDD', border:'none', color:customVariableEditor ? '#9CA3AF' : '#fff', borderRadius:8, padding:'8px 12px', fontSize:12, fontWeight:800, cursor:orderTableSettingsSaving || customVariableEditor ? 'not-allowed' : 'pointer' }}>{orderTableSettingsSaving ? 'Saving...' : 'Save columns'}</button>
                </div>
              </div>
              <div style={{ border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
                <div style={{ padding:'14px 16px', borderBottom:'1px solid #F3F4F6' }}>
                  <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E' }}>Visible orders</div>
                  <div style={{ fontSize:12, color:'#6B7280', marginTop:3 }}>
                    Apply actions to the {filteredIds.length} order{filteredIds.length === 1 ? '' : 's'} matching the current filters.
                  </div>
                </div>
                <div style={{ padding:'14px 16px', display:'grid', gap:10 }}>
                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setVisibleFulfilled(true)}
                      disabled={!filteredIds.length || visibleFulfillmentWorking}
                      style={{ background:'#F0FDF4', color:filteredIds.length ? '#166534' : '#9CA3AF', border:'1px solid #BBF7D0', borderRadius:8, padding:'8px 11px', fontSize:12, fontWeight:800, cursor:!filteredIds.length || visibleFulfillmentWorking ? 'not-allowed' : 'pointer', display:'inline-flex', alignItems:'center', gap:6, opacity:visibleFulfillmentWorking ? 0.65 : 1 }}
                    >
                      <CheckCircle2 size={14} strokeWidth={2.2}/>
                      {visibleFulfillmentWorking === 'fulfilled' ? 'Saving...' : 'Mark visible fulfilled'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVisibleFulfilled(false)}
                      disabled={!filteredIds.length || visibleFulfillmentWorking}
                      style={{ background:'#fff', color:filteredIds.length ? '#374151' : '#9CA3AF', border:'1px solid #D1D5DB', borderRadius:8, padding:'8px 11px', fontSize:12, fontWeight:800, cursor:!filteredIds.length || visibleFulfillmentWorking ? 'not-allowed' : 'pointer', display:'inline-flex', alignItems:'center', gap:6, opacity:visibleFulfillmentWorking ? 0.65 : 1 }}
                    >
                      <XCircle size={14} strokeWidth={2.2}/>
                      {visibleFulfillmentWorking === 'unfulfilled' ? 'Saving...' : 'Mark visible unfulfilled'}
                    </button>
                  </div>
                  <div style={{ paddingTop:10, borderTop:'1px solid #F3F4F6' }}>
                    <button
                      type="button"
                      onClick={() => { setSettingsOpen(false); runAiAudit(); }}
                      disabled={aiAuditLoading || activeOrderCount === 0}
                      title="Let AI scan the order table for discrepancies"
                      style={{ background:'#fff', color:aiAuditLoading || activeOrderCount === 0 ? '#9CA3AF' : '#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'8px 11px', fontSize:12, fontWeight:800, cursor:aiAuditLoading || activeOrderCount === 0 ? 'not-allowed' : 'pointer', display:'inline-flex', alignItems:'center', gap:6 }}
                    >
                      <Sparkles size={14} strokeWidth={2.2}/>
                      {aiAuditLoading ? 'Analyzing...' : 'AI check'}
                    </button>
                  </div>
                </div>
              </div>
              {settingsMessage && <div style={{ fontSize:12, color:'#166534', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'9px 10px', fontWeight:700 }}>{settingsMessage}</div>}
              {settingsError && <div style={{ fontSize:12, color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'9px 10px', fontWeight:700 }}>{settingsError}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
